import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import { FaDownload, FaEye, FaCalendarAlt, FaUsers, FaMoneyBillWave, FaFileExport, FaEdit, FaSearch, FaPrint, FaTable, FaTh, FaExclamationTriangle, FaTimes, FaWhatsapp, FaTrash, FaFileAlt, FaHandHoldingUsd, FaQuestionCircle, FaExchangeAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import Select from 'react-select';

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const getCommissionMetrics = (item) => {
  const quantity = Number(item?.quantity) || 0;
  const rate = Number(item?.rate) || 0;
  const discount = Number(item?.discount) || 0;
  const ttType = item?.tt?.type === 'fixed' ? 'fixed' : 'percentage';
  const ttValue = Number(item?.tt?.value) || 0;
  const integratedTaxPercentage = Number(item?.integratedTax ?? item?.itemId?.integratedTax ?? 0) || 0;

  const amountBeforeDiscount = rate * quantity;
  const totalItemValue = amountBeforeDiscount - (amountBeforeDiscount * discount / 100);
  const itemValueWithoutGST = (totalItemValue * 100) / (100 + integratedTaxPercentage);

  const uncappedAmount = ttType === 'fixed'
    ? ttValue
    : (itemValueWithoutGST * ttValue) / 100;
  const calculatedAmount = round2(Math.max(0, Math.min(uncappedAmount, itemValueWithoutGST)));

  return {
    totalItemValue,
    integratedTaxPercentage,
    itemValueWithoutGST,
    calculatedAmount,
    isFixedCapped: ttType === 'fixed' && ttValue > itemValueWithoutGST,
  };
};

const getReferencePaymentState = (reference) => {
  const commission = Number(reference?.totalCommission) || 0;
  const paid = Number(reference?.totalPaid) || 0;
  const pending = Number(reference?.pendingCommission ?? (commission - paid)) || 0;

  if (commission <= 0) return 'no-commission';
  if (pending > 0) return 'to-be-paid';
  return 'paid';
};

const ReferenceCommission = () => {
  const { user } = useContext(AuthContext);
  
  console.log('🎯 ReferenceCommission component mounted/rendered');
  console.log('👤 User context:', user);

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setIsDarkMode(savedMode === 'true');
    }, 500);

    return () => clearInterval(interval);
  }, []);
  
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const [referenceDetails, setReferenceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Commission assignment states
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedOrderForCommission, setSelectedOrderForCommission] = useState(null);
  const [commissionAssignmentLoading, setCommissionAssignmentLoading] = useState(false);
  const [editableCommissionData, setEditableCommissionData] = useState(null);
  const [removeCommissionLoading, setRemoveCommissionLoading] = useState(false);

  // Reference assignment states
  const [showReferenceAssignModal, setShowReferenceAssignModal] = useState(false);
  const [selectedOrderForReference, setSelectedOrderForReference] = useState(null);
  const [referenceAssignmentLoading, setReferenceAssignmentLoading] = useState(false);
  const [availableReferences, setAvailableReferences] = useState([]);
  const [selectedReferenceId, setSelectedReferenceId] = useState('');

  // Delivery orders without references (for bulk modal only)
  const [deliveryOrdersWithoutReferences, setDeliveryOrdersWithoutReferences] = useState([]);
  const [loadingOrdersWithoutReferences, setLoadingOrdersWithoutReferences] = useState(false);

  // Paid commission states
  const [showPaidCommissionModal, setShowPaidCommissionModal] = useState(false);
  const [paidCommissionOrders, setPaidCommissionOrders] = useState([]);
  const [paidCommissionForm, setPaidCommissionForm] = useState({
    orderAmounts: {},
    paymentDate: new Date().toISOString().split('T')[0],
    paymentModes: [],
    remarks: '',
    claimReceivable: true,
  });
  const [paidCommissionLoading, setPaidCommissionLoading] = useState(false);
  const [selectedOrdersForReport, setSelectedOrdersForReport] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Bulk reference assignment modal
  const [showBulkReferenceModal, setShowBulkReferenceModal] = useState(false);
  const [bulkAssignmentLoading, setBulkAssignmentLoading] = useState(false);
  const [selectedOrdersForBulkAssignment, setSelectedOrdersForBulkAssignment] = useState([]);
  const [bulkModalDateRange, setBulkModalDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Transfer reference modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferOrder, setTransferOrder] = useState(null);
  const [transferTargetRefId, setTransferTargetRefId] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  
  // Search state for delivery orders
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDeliveryOrders, setFilteredDeliveryOrders] = useState([]);

  // Search state for references table
  const [referenceSearchQuery, setReferenceSearchQuery] = useState('');

  // Payment status filter
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  // Paid report selection modal
  const [showPaidReportModal, setShowPaidReportModal] = useState(false);
  const [selectedPaidReportOrders, setSelectedPaidReportOrders] = useState([]);
  const [paidReportShowInventory, setPaidReportShowInventory] = useState(false);

  // To-be-paid report selection modal
  const [showToBePaidReportModal, setShowToBePaidReportModal] = useState(false);
  const [selectedToBePaidOrders, setSelectedToBePaidOrders] = useState([]);

  // Remove payment modal
  const [showRemovePaymentModal, setShowRemovePaymentModal] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [removePaymentLoading, setRemovePaymentLoading] = useState(false);
  const [removePaymentDateFrom, setRemovePaymentDateFrom] = useState('');
  const [removePaymentDateTo, setRemovePaymentDateTo] = useState('');

  // Memo Issued modal
  const [showMemoIssuedModal, setShowMemoIssuedModal] = useState(false);
  const [memoIssuedList, setMemoIssuedList] = useState([]);
  const [selectedMemoKeys, setSelectedMemoKeys] = useState([]);
  const [memoIssuedLoading, setMemoIssuedLoading] = useState(false);
  const [memoIssuedDateFrom, setMemoIssuedDateFrom] = useState('');
  const [memoIssuedDateTo, setMemoIssuedDateTo] = useState('');

  // GR Commission Collection (Record Payment) modal
  const [showGRPaymentModal, setShowGRPaymentModal] = useState(false);
  const [grPaymentReference, setGRPaymentReference] = useState(null);
  const [recoveryEntries, setRecoveryEntries] = useState([]);
  const [grPaymentLoading, setGRPaymentLoading] = useState(false);
  const [grPaymentForms, setGRPaymentForms] = useState({}); // { [grId]: { amount, date, modes[], remarks } }
  const PAYMENT_MODES = ['Cash', 'Bank Transfer', 'UPI', 'Cheque'];

  // Summary stats
  const [summary, setSummary] = useState({
    totalReferences: 0,
    totalCommission: 0,
    totalPaid: 0,
    totalReceivable: 0
  });

  // View mode state (table or card)
  const [viewMode, setViewMode] = useState('table');

  // Cancelled DOs section toggle in details modal
  const [showCancelledOrders, setShowCancelledOrders] = useState(false);

  // Help modal
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Helper function for dark mode select styles
  const getSelectStyles = (isDark) => ({
    control: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      borderColor: isDark ? '#4B5563' : '#D1D5DB',
      color: isDark ? '#F3F4F6' : '#111827',
      minHeight: '44px',
      '@media (min-width: 640px)': {
        minHeight: '38px'
      },
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#9CA3AF'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : '#ffffff',
      border: isDark ? '1px solid #4B5563' : '1px solid #D1D5DB',
      zIndex: 9999
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (isDark ? '#4B5563' : '#E5E7EB')
        : (isDark ? '#374151' : '#ffffff'),
      color: isDark ? '#F3F4F6' : '#111827',
      '&:hover': {
        backgroundColor: isDark ? '#4B5563' : '#E5E7EB'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? '#4B5563' : '#E5E7EB'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#111827'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280',
      '&:hover': {
        backgroundColor: isDark ? '#EF4444' : '#FCA5A5',
        color: isDark ? '#ffffff' : '#7F1D1D'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  // Helper function to get dark mode classes
  const getDarkModeClasses = () => ({
    bg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    border: isDarkMode ? 'border-gray-600' : 'border-gray-300',
    input: isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900',
    card: isDarkMode ? 'bg-gray-700' : 'bg-white',
    cardBorder: isDarkMode ? 'border-gray-600' : 'border-gray-200',
    tableHeader: isDarkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRow: isDarkMode ? 'bg-gray-800' : 'bg-white',
    tableRowHover: isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    modal: isDarkMode ? 'bg-gray-800' : 'bg-white',
    modalBorder: isDarkMode ? 'border-gray-600' : 'border-gray-200'
  });

  // Get company ID from user context
  const getCompanyId = () => {
    // Try multiple possible paths
    if (user?.user?.company?._id) {
      console.log('🏢 Using company._id:', user.user.company._id);
      return user.user.company._id;
    }
    if (user?.user?.company) {
      console.log('🏢 Using company directly:', user.user.company);
      return user.user.company;
    }
    console.warn('⚠️ No company ID found in user context');
    return null;
  };
  
  const companyId = getCompanyId();

  const api = useMemo(() => {
    const baseURL = `${process.env.REACT_APP_API_BASE_URL}/reference-commission`;
    console.log('🌐 API base URL:', baseURL);
    
    const instance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add retry interceptor for transient failures
    instance.interceptors.response.use(null, async (error) => {
      const config = error.config;
      if (!config || config._retryCount >= 2) return Promise.reject(error);
      const isRetryable = !error.response || error.code === 'ECONNABORTED' || error.response.status >= 500;
      if (!isRetryable) return Promise.reject(error);
      config._retryCount = (config._retryCount || 0) + 1;
      console.log(`🔄 Retrying request (${config._retryCount}/2): ${config.url}`);
      await new Promise(r => setTimeout(r, 1000 * config._retryCount));
      return instance(config);
    });

    return instance;
  }, []);

  // Fetch references summary
  const fetchReferencesSummary = async () => {
    console.log('🎯 fetchReferencesSummary called');
    if (!selectedDateRange.startDate || !selectedDateRange.endDate || isNaN(new Date(selectedDateRange.startDate)) || isNaN(new Date(selectedDateRange.endDate))) {
      console.warn('⚠️ Invalid date range, skipping fetch');
      return;
    }
    try {
      setLoading(true);
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      
      if (companyId) {
        params.companyId = companyId;
      }

      console.log('🔍 Fetching references summary with params:', params);
      console.log('🏢 Company ID from user context:', companyId);

      const response = await api.get('/references-summary', { params });
      
      console.log('📊 API Response:', response.data);
      console.log('📋 References received:', response.data.references?.length || 0);

      if (response.data.success) {
        setReferences(response.data.references);
        setSummary({
          totalReferences: response.data.totalReferences,
          totalCommission: response.data.totalCommission,
          totalPaid: response.data.totalPaid || 0,
          totalReceivable: response.data.totalReceivable || 0
        });
      } else {
        console.warn('⚠️ API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching references summary:', error);
      console.error('❌ Error response:', error.response?.data);
      const isTimeout = error.code === 'ECONNABORTED';
      const msg = isTimeout
        ? 'Request timed out. The server may be busy.'
        : error.response?.data?.message || 'Failed to fetch references data';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: msg,
        showConfirmButton: true,
        confirmButtonText: 'Retry',
        showCancelButton: true,
        customClass: { container: '!z-[99999]' }
      }).then((result) => {
        if (result.isConfirmed) fetchReferencesSummary();
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch reference details
  const fetchReferenceDetails = async (referenceId) => {
    try {
      setDetailsLoading(true);
      setSelectedOrdersForReport([]);
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      
      if (companyId) {
        params.companyId = companyId;
      }

      const response = await api.get(`/reference-details/${referenceId}`, { params });
      
      if (response.data.success) {
        setReferenceDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reference details:', error);
      alert('Failed to fetch reference details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Export goods returns (commission collection / auto-deduction status)
  const exportGoodsReturnsToExcel = async () => {
    try {
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      if (companyId) params.companyId = companyId;

      const response = await api.get('/export-goods-returns', {
        params,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `goods-returns-${selectedDateRange.startDate}-to-${selectedDateRange.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting goods returns:', error);
      alert('Failed to export goods returns');
    }
  };

  // Export references summary
  const exportSummaryToExcel = async () => {
    try {
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      
      if (companyId) {
        params.companyId = companyId;
      }

      const response = await api.get('/export-references-summary', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `references-commission-summary-${selectedDateRange.startDate}-to-${selectedDateRange.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting summary:', error);
      alert('Failed to export summary');
    }
  };

  // Export reference details
  const exportReferenceDetails = async (referenceId, referenceName) => {
    try {
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      
      if (companyId) {
        params.companyId = companyId;
      }

      const response = await api.get(`/export-reference-details/${referenceId}`, {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reference-commission-details-${referenceName.replace(/[^a-zA-Z0-9]/g, '-')}-${selectedDateRange.startDate}-to-${selectedDateRange.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting reference details:', error);
      alert('Failed to export reference details');
    }
  };

  // Print commission statement (PDF)
  const printCommissionStatement = (referenceId, referenceName) => {
    try {
      const params = new URLSearchParams({
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      });
      if (companyId) {
        params.append('companyId', companyId);
      }
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/reference-commission-statement/${referenceId}?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-reference-commission-statement.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing commission statement:', error);
      alert('Failed to print commission statement');
    }
  };

  // Open unified Receivable Recovery modal
  const openRecoveryModal = async (reference) => {
    // Guard: fall back to the selected reference (e.g. if called from an event handler)
    if (!reference?._id && selectedReference) reference = selectedReference;
    if (!reference?._id) return;
    setGRPaymentReference(reference);
    setGRPaymentLoading(true);
    setShowGRPaymentModal(true);
    setGRPaymentForms({});
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/open-receivables/${reference._id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const entries = resp.data.data || [];
      setRecoveryEntries(entries);
      const forms = {};
      entries.forEach(e => {
        forms[e._id] = {
          amount: '',
          date: new Date().toISOString().split('T')[0],
          modes: [],
          remarks: '',
        };
      });
      setGRPaymentForms(forms);
    } catch (err) {
      console.error('Failed to load receivables:', err);
      Swal.fire('Error', err?.response?.data?.message || 'Failed to load receivables', 'error');
      setShowGRPaymentModal(false);
    } finally {
      setGRPaymentLoading(false);
    }
  };

  const saveRecoveryCash = async (entryId) => {
    const form = grPaymentForms[entryId];
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      Swal.fire('Validation', 'Enter a valid amount greater than 0', 'warning'); return;
    }
    if (!form.modes.length) {
      Swal.fire('Validation', 'Select at least one payment mode', 'warning'); return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/receivable-cash/${entryId}`,
        {
          amount,
          date: form.date,
          paymentModes: form.modes,
          remarks: form.remarks,
          userId: user?.user?._id,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      Swal.fire({ title: 'Saved', text: 'Cash recovery recorded.', icon: 'success', timer: 1500, showConfirmButton: false });
      // Refresh the list
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/open-receivables/${grPaymentReference._id}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setRecoveryEntries(resp.data.data || []);
      setGRPaymentForms(prev => {
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
      if (selectedReference) fetchReferenceDetails(selectedReference._id);
    } catch (err) {
      console.error('Failed to record recovery:', err);
      Swal.fire('Error', err?.response?.data?.message || 'Failed to record recovery', 'error');
    }
  };

  // Print GR commission statement (PDF)
  const printGRReport = (referenceId) => {
    try {
      const params = new URLSearchParams({
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      });
      if (companyId) {
        params.append('companyId', companyId);
      }
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/goods-return-statement/${referenceId}?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-gr-commission-report.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing GR report:', error);
      alert('Failed to print GR report');
    }
  };

  // Print paid commission report (PDF)
  const printPaidCommissionReport = (referenceId) => {
    try {
      const params = new URLSearchParams({
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      });
      if (companyId) {
        params.append('companyId', companyId);
      }
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/paid-commission-report/${referenceId}?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-paid-commission-report.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing paid commission report:', error);
      alert('Failed to print paid commission report');
    }
  };

  // Print paid commission report for selected delivery orders
  const printSelectedOrdersPaidCommissionReport = () => {
    if (selectedOrdersForReport.length === 0) {
      Swal.fire('No Orders Selected', 'Please select at least one delivery order to generate the report', 'warning');
      return;
    }
    try {
      const params = new URLSearchParams();
      selectedOrdersForReport.forEach(id => params.append('orderIds', id));
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/paid-commission-report/orders?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-paid-commission-report.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing selected orders paid commission report:', error);
      alert('Failed to print paid commission report');
    }
  };

  const toggleOrderForReport = (orderId) => {
    setSelectedOrdersForReport(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleAllOrdersForReport = () => {
    if (!referenceDetails) return;
    const eligibleOrders = referenceDetails.deliveryOrders.filter(o => o.paidCommissions && o.paidCommissions.length > 0);
    if (selectedOrdersForReport.length === eligibleOrders.length) {
      setSelectedOrdersForReport([]);
    } else {
      setSelectedOrdersForReport(eligibleOrders.map(o => o._id));
    }
  };

  // Open paid report selection modal
  const openPaidReportModal = () => {
    if (!referenceDetails) return;
    const paidOrders = referenceDetails.deliveryOrders.filter(o => o.paidCommissions && o.paidCommissions.length > 0);
    if (paidOrders.length === 0) {
      Swal.fire('No Paid Orders', 'No delivery orders have paid commissions to report', 'info');
      return;
    }
    setSelectedPaidReportOrders(paidOrders.map(o => o._id));
    setShowPaidReportModal(true);
  };

  const printPaidReportFromModal = () => {
    if (selectedPaidReportOrders.length === 0) {
      Swal.fire('No Orders Selected', 'Please select at least one delivery order', 'warning');
      return;
    }
    try {
      const params = new URLSearchParams();
      selectedPaidReportOrders.forEach(id => params.append('orderIds', id));
      if (paidReportShowInventory) params.append('showInventory', 'true');
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/paid-commission-report/orders?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-paid-commission-report.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
      setShowPaidReportModal(false);
    } catch (error) {
      console.error('Error printing paid report:', error);
      Swal.fire('Error', 'Failed to print paid commission report', 'error');
    }
  };

  const printPaymentMemo = (batchId) => {
    if (!batchId) {
      Swal.fire('No Memo', 'Payment memo is not available for this record', 'info');
      return;
    }
    try {
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/payment-memo/${batchId}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-payment-memo.hbs&id_field=reference.name&format=A5&margintop=0.2cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing payment memo:', error);
      Swal.fire('Error', 'Failed to print payment memo', 'error');
    }
  };

  const openMemoIssuedModal = () => {
    setSelectedMemoKeys([]);
    setMemoIssuedDateFrom(selectedDateRange?.startDate || '');
    setMemoIssuedDateTo(selectedDateRange?.endDate || '');
    setShowMemoIssuedModal(true);
  };

  const fetchMemoIssuedList = async () => {
    if (!selectedReference?._id) return;
    try {
      setMemoIssuedLoading(true);
      const params = {};
      if (memoIssuedDateFrom) params.startDate = memoIssuedDateFrom;
      if (memoIssuedDateTo) params.endDate = memoIssuedDateTo;
      if (companyId) params.companyId = companyId;
      const response = await api.get(`/payment-memo/reference/${selectedReference._id}`, { params });
      setMemoIssuedList(response.data?.memos || []);
    } catch (error) {
      console.error('Error fetching issued memos:', error);
      setMemoIssuedList([]);
    } finally {
      setMemoIssuedLoading(false);
    }
  };

  const printSelectedMemos = () => {
    if (!selectedReference?._id) return;
    if (selectedMemoKeys.length === 0) {
      Swal.fire('No Selection', 'Please select at least one memo to print', 'warning');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('keys', selectedMemoKeys.join(','));
      if (companyId) params.append('companyId', companyId);
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/payment-memo/reference/${selectedReference._id}?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-payment-memo.hbs&id_field=reference.name&format=A5&margintop=0.2cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error printing memos:', error);
      Swal.fire('Error', 'Failed to print memos', 'error');
    }
  };

  useEffect(() => {
    if (showMemoIssuedModal && selectedReference?._id) {
      fetchMemoIssuedList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMemoIssuedModal, memoIssuedDateFrom, memoIssuedDateTo]);

  // Open to-be-paid report selection modal
  const openToBePaidReportModal = () => {
    if (!referenceDetails) return;
    const pendingOrders = referenceDetails.deliveryOrders.filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0);
    if (pendingOrders.length === 0) {
      Swal.fire('No Pending', 'All commissions are fully paid', 'info');
      return;
    }
    setSelectedToBePaidOrders(pendingOrders.map(o => o._id));
    setShowToBePaidReportModal(true);
  };

  const printToBePaidReportFromModal = () => {
    if (selectedToBePaidOrders.length === 0) {
      Swal.fire('No Orders Selected', 'Please select at least one delivery order', 'warning');
      return;
    }
    try {
      const params = new URLSearchParams();
      selectedToBePaidOrders.forEach(id => params.append('orderIds', id));
      const dataUrl = encodeURIComponent(
        `${process.env.REACT_APP_API_BASE_URL}/reference-commission/to-be-paid-report/orders?${params.toString()}`
      );
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '')}/pdf/convert?fileName=rlugroup-to-be-paid-commission-report.hbs&id_field=reference.name&format=A4&margintop=0.5cm&url=${dataUrl}`;
      window.open(pdfUrl, '_blank');
      setShowToBePaidReportModal(false);
    } catch (error) {
      console.error('Error printing to-be-paid report:', error);
      Swal.fire('Error', 'Failed to print to-be-paid commission report', 'error');
    }
  };

  // Send commission report via WhatsApp
  const handleWhatsAppSend = async (reference, reportType = 'statement') => {
    try {
      const checkRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/check-automation/reference-commission`,
        { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!checkRes.data.data.isActive) {
        return Swal.fire('Not Enabled', 'Reference commission WhatsApp send is not enabled. Please ask your Company Head to enable it in WhatsApp Automations.', 'warning');
      }
      if (!checkRes.data.data.isConnected) {
        const reconnectResult = await Swal.fire({
          icon: 'warning',
          title: 'WhatsApp Not Connected',
          text: 'WhatsApp is not connected. Would you like to try reconnecting?',
          showCancelButton: true,
          confirmButtonText: 'Try Reconnect',
          confirmButtonColor: '#25D366',
          cancelButtonText: 'Cancel'
        });
        if (!reconnectResult.isConfirmed) return;

        Swal.fire({ title: 'Reconnecting...', text: 'Attempting to restore WhatsApp connection...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        try {
          const reconnectRes = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/try-reconnect`,
            {},
            { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          if (!reconnectRes.data.success) {
            return Swal.fire('Reconnect Failed', reconnectRes.data.message || 'Could not reconnect WhatsApp.', 'error');
          }
          Swal.close();
        } catch (reconnectErr) {
          return Swal.fire('Reconnect Failed', reconnectErr.response?.data?.message || 'Could not reconnect WhatsApp. Please ask your Company Head to connect.', 'error');
        }
      }

      const refName = reference.referenceName || reference.name || 'Reference';
      const refPhone = reference.referencePhone || reference.phoneNumber || '';

      const { value: formValues } = await Swal.fire({
        title: 'Send Commission Report via WhatsApp',
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p style="margin-bottom: 12px;"><strong>Reference:</strong> ${refName}</p>
            <label style="display: block; margin-bottom: 4px; font-weight: 600;">Report Type:</label>
            <select id="swal-report-type" class="swal2-select" style="margin: 0 0 12px 0; width: 100%; box-sizing: border-box; padding: 8px; border: 1px solid #d1d5db; border-radius: 6px;">
              <option value="statement" ${reportType === 'statement' ? 'selected' : ''}>Commission Statement</option>
              <option value="paid" ${reportType === 'paid' ? 'selected' : ''}>Paid Commission Report</option>
            </select>
            <label style="display: block; margin-bottom: 4px; font-weight: 600;">Phone Number:</label>
            <input id="swal-phone" class="swal2-input" placeholder="Enter phone number" value="${refPhone}" style="margin: 0 0 8px 0; width: 100%; box-sizing: border-box;">
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Send via WhatsApp',
        confirmButtonColor: '#25D366',
        cancelButtonText: 'Cancel',
        preConfirm: () => {
          const phone = document.getElementById('swal-phone').value.trim();
          const selectedReportType = document.getElementById('swal-report-type').value;
          if (!phone) {
            Swal.showValidationMessage('Phone number is required');
            return false;
          }
          return { phone, reportType: selectedReportType };
        }
      });

      if (!formValues) return;

      Swal.fire({
        title: 'Sending...',
        text: 'Generating PDF and queuing message...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/whatsapp-automations/send-commission-report`,
        {
          referenceId: reference._id,
          phone: formValues.phone,
          reportType: formValues.reportType,
          startDate: selectedDateRange.startDate,
          endDate: selectedDateRange.endDate
        },
        { withCredentials: true, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        Swal.fire('Queued!', response.data.message, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('WhatsApp send error:', error);
      Swal.fire('Error', error.response?.data?.message || error.message || 'Failed to send commission report', 'error');
    }
  };

  // Export incorrect commissions (TT Audit)
  const exportIncorrectCommissions = async () => {
    try {
      const params = {
        startDate: selectedDateRange.startDate,
        endDate: selectedDateRange.endDate
      };
      if (companyId) {
        params.companyId = companyId;
      }

      const response = await api.get('/export-incorrect-commissions', {
        params,
        responseType: 'blob'
      });

      // Check if the response is JSON (no issues found) vs Excel blob
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Backend returned JSON — means no issues found
        const text = await response.data.text();
        const jsonData = JSON.parse(text);
        Swal.fire({
          icon: 'success',
          title: 'TT Audit Complete',
          text: jsonData.message || 'No incorrect commissions found.',
          confirmButtonColor: '#6366f1'
        });
        return;
      }

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tt-audit-incorrect-commissions-${selectedDateRange.startDate}-to-${selectedDateRange.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'warning',
        title: 'TT Audit Report Downloaded',
        html: 'Check the Excel file for commission discrepancies.<br/><small>Includes GST rate change impact analysis.</small>',
        confirmButtonColor: '#6366f1'
      });
    } catch (error) {
      console.error('Error exporting incorrect commissions:', error);
      Swal.fire({
        icon: 'error',
        title: 'TT Audit Failed',
        text: 'Failed to export incorrect commissions report. Please try again.',
        confirmButtonColor: '#6366f1'
      });
    }
  };

  // View reference details
  const viewReferenceDetails = async (reference) => {
    setSelectedReference(reference);
    setShowDetailsModal(true);
    await Promise.all([
      fetchReferenceDetails(reference._id),
      availableReferences.length === 0 ? fetchAvailableReferences() : Promise.resolve(),
    ]);
  };

  // Close details modal
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedReference(null);
    setReferenceDetails(null);
    setShowTransferModal(false);
    setTransferOrder(null);
    setTransferTargetRefId('');
  };

  // Open commission assignment modal
  const openCommissionAssignmentModal = (order) => {
    if (order.commissionBlockedByReturn) {
      Swal.fire({
        icon: 'info',
        title: 'Commission Not Applicable',
        text: order.commissionBlockedReason || 'This order has been fully returned, so commission on net quantity is zero.',
        confirmButtonColor: '#6366f1'
      });
      return;
    }

    console.log('🚀 Opening Commission Modal for Order:', {
      orderId: order._id,
      doNumber: order.doNumber,
      totalTTAmount: order.totalTTAmount,
      itemsWithTT: order.items.map(item => ({
        name: item.name,
        existingTT: item.tt,
        rate: item.rate,
        quantity: item.quantity,
        totalValue: item.rate * item.quantity
      }))
    });

    setSelectedOrderForCommission(order);
    // Initialize editable commission data
    const editableData = {
      orderId: order._id,
      doNumber: order.doNumber,
      items: order.items.map(item => {
        const editableItem = {
          ...item,
          itemId: item.itemId?._id || item.itemId,
          integratedTax: Number(item.integratedTax ?? item.itemId?.integratedTax ?? 0) || 0,
        };

        const ttData = {
          type: item.tt?.type || 'percentage',
          value: item.tt?.value || 0,
          calculatedAmount: 0
        };

        editableItem.tt = ttData;
        editableItem.tt.calculatedAmount = getCommissionMetrics(editableItem).calculatedAmount;
        
        console.log(`📋 Item: ${item.name} - Loading TT Data:`, ttData);
        
        return editableItem;
      })
    };
    setEditableCommissionData(editableData);
    setShowCommissionModal(true);
  };

  // Close commission assignment modal
  const closeCommissionAssignmentModal = () => {
    setShowCommissionModal(false);
    setSelectedOrderForCommission(null);
    setEditableCommissionData(null);
  };

  // Handle commission value change for an item
  const handleCommissionChange = (itemIndex, field, value) => {
    console.log('🔄 Commission Change Input:', {
      itemIndex,
      field,
      rawValue: value,
      valueType: typeof value
    });
    
    setEditableCommissionData(prev => {
      const newData = {
        ...prev,
        items: prev.items.map((existingItem, index) => (
          index === itemIndex
            ? { ...existingItem, tt: { ...(existingItem.tt || {}) } }
            : existingItem
        ))
      };
      const item = newData.items[itemIndex];
      
      if (field === 'tt.type') {
        item.tt.type = value;
      } else if (field === 'tt.value') {
        const parsedValue = parseFloat(value) || 0;
        console.log('📊 Value Parsing:', {
          original: value,
          parsed: parsedValue,
          comparison: `"${value}" → ${parsedValue}`
        });
        item.tt.value = parsedValue;
      }

      const { totalItemValue, integratedTaxPercentage, itemValueWithoutGST, calculatedAmount } = getCommissionMetrics(item);
      
      console.log('🔧 Commission Calculation Debug:', {
        itemName: item.name,
        totalItemValue,
        integratedTaxPercentage,
        itemValueWithoutGST,
        ttType: item.tt.type,
        ttValue: item.tt.value
      });

      item.tt.calculatedAmount = calculatedAmount;

      console.log('💰 Final Calculated Amount:', item.tt.calculatedAmount);

      return newData;
    });
  };

  // Save commission assignments
  const saveCommissionAssignments = async () => {
    try {
      setCommissionAssignmentLoading(true);
      
      // Calculate total before sending
      const totalCalculatedAmount = editableCommissionData.items.reduce(
        (sum, item) => sum + getCommissionMetrics(item).calculatedAmount,
        0
      );
      
      const commissionsToSave = editableCommissionData.items.map((item, index) => {
        const { calculatedAmount: recalculatedAmount } = getCommissionMetrics(item);
        
        console.log(`🔍 Item "${item.name}" [index ${index}] - Save Verification:`, {
          originalCalculatedAmount: item.tt.calculatedAmount,
          recalculatedAmount: recalculatedAmount,
          difference: Math.abs(item.tt.calculatedAmount - recalculatedAmount),
          match: Math.abs(item.tt.calculatedAmount - recalculatedAmount) < 0.01
        });
        
        return {
          index,
          itemId: item.itemId,
          tt: {
            type: item.tt.type,
            value: parseFloat(item.tt.value), // Ensure it's a number
            calculatedAmount: parseFloat(recalculatedAmount.toFixed(2)) // Use recalculated and ensure it's a number
          }
        };
      });
      
      console.log('💾 === SAVE COMMISSION DEBUG START ===');
      console.log('🎯 Order ID:', editableCommissionData.orderId);
      console.log('📊 Pre-Save Total Calculated:', totalCalculatedAmount.toFixed(2));
      console.log('📋 Items to Save:', commissionsToSave.map(item => ({
        itemId: item.itemId,
        ttType: item.tt.type,
        ttValue: item.tt.value,
        calculatedAmount: item.tt.calculatedAmount
      })));
      console.log('💾 === SENDING TO BACKEND ===');
      
      const response = await api.put(`/assign-commission/${editableCommissionData.orderId}`, {
        items: commissionsToSave,
        userId: user.user._id
      });

      const savedTotal = Number(response.data?.verifiedTotalTT ?? response.data?.totalTTAmount ?? totalCalculatedAmount) || 0;

      console.log('📨 Backend Response:', response.data);
      console.log('💾 === SAVE COMMISSION DEBUG END ===');

      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Commission Saved!',
          text: `Commission assignments saved successfully! Total: ₹${savedTotal.toFixed(2)}`,
          confirmButtonColor: '#10B981'
        });
        
        closeCommissionAssignmentModal();
        
        // Refresh the reference details
        if (selectedReference) {
          await fetchReferenceDetails(selectedReference._id);
        }
        // Refresh the main summary
        await fetchReferencesSummary();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Save Failed',
          text: response.data.message || 'Failed to save commission assignments',
          confirmButtonColor: '#EF4444'
        });
      }
    } catch (error) {
      console.error('❌ Error saving commission assignments:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: error.response?.data?.message || 'Failed to save commission assignments',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setCommissionAssignmentLoading(false);
    }
  };

  const removeCommissionForOrder = async (order) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Remove Commission?',
      text: order.removeCommissionBlockedReason || order.commissionBlockedReason || 'This will reset the commission to zero for this fully returned order.',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Remove It',
    });

    if (!result.isConfirmed) return;

    try {
      setRemoveCommissionLoading(true);
      const response = await api.put(`/remove-commission/${order._id}`, {
        userId: user.user._id,
      });

      await Swal.fire({
        icon: 'success',
        title: 'Commission Removed',
        text: `Commission reset successfully. Total: ₹${Number(response.data?.totalTTAmount || 0).toFixed(2)}`,
        confirmButtonColor: '#10B981'
      });

      if (selectedReference) {
        await fetchReferenceDetails(selectedReference._id);
      }
      await fetchReferencesSummary();
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Remove Failed',
        text: error.response?.data?.message || 'Failed to remove commission',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setRemoveCommissionLoading(false);
    }
  };

  // Fetch delivery orders without references
  const fetchDeliveryOrdersWithoutReferences = async (customDateRange = null, page = 1, search = '') => {
    try {
      setLoadingOrdersWithoutReferences(true);
      const dateRange = customDateRange || bulkModalDateRange;
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: page,
        limit: 100,
        includeReferenced: true
      };
      
      if (companyId) {
        params.companyId = companyId;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      console.log('🔍 Fetching delivery orders without references - Page:', page, 'Params:', params);

      const response = await api.get('/delivery-orders-without-references', { params });
      
      if (response.data.success) {
        setDeliveryOrdersWithoutReferences(response.data.deliveryOrders);
        setPagination(response.data.pagination);
        console.log('📋 Delivery orders without references received:', response.data.deliveryOrders.length, 'Total:', response.data.pagination.totalCount);
      } else {
        console.warn('⚠️ Failed to fetch delivery orders without references:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching delivery orders without references:', error);
    } finally {
      setLoadingOrdersWithoutReferences(false);
    }
  };

  // Fetch available references for assignment
  const fetchAvailableReferences = async () => {
    try {
      const params = {};
      if (companyId) {
        params.companyId = companyId;
      }

      const response = await api.get('/references-list', { params });
      
      if (response.data.success) {
        setAvailableReferences(response.data.references);
      } else {
        console.warn('⚠️ Failed to fetch available references:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching available references:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load available references',
        confirmButtonColor: '#EF4444'
      });
    }
  };

  // Open reference assignment modal
  const openReferenceAssignmentModal = async (order) => {
    console.log('🎯 Opening reference assignment modal for order:', order.doNumber);
    
    setSelectedOrderForReference(order);
    setSelectedReferenceId(order.reference?._id || '');
    
    // Fetch available references
    await fetchAvailableReferences();
    
    setShowReferenceAssignModal(true);
  };

  // Close reference assignment modal
  const closeReferenceAssignmentModal = () => {
    setShowReferenceAssignModal(false);
    setSelectedOrderForReference(null);
    setSelectedReferenceId('');
    setAvailableReferences([]);
  };

  // Assign reference to delivery order
  const assignReferenceToOrder = async () => {
    if (!selectedOrderForReference || !selectedReferenceId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a reference to assign',
        confirmButtonColor: '#F59E0B'
      });
      return;
    }

    try {
      setReferenceAssignmentLoading(true);

      const payload = {
        referenceId: selectedReferenceId,
        userId: user?.user?._id
      };

      console.log('🎯 Assigning reference with payload:', payload);

      const response = await api.put(`/assign-reference/${selectedOrderForReference._id}`, payload);
      
      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Reference Assigned!',
          text: response.data.message,
          confirmButtonColor: '#10B981'
        });

        // Close modal
        closeReferenceAssignmentModal();

        // Refresh the reference details if we're viewing details
        if (selectedReference) {
          await fetchReferenceDetails(selectedReference._id);
        }
        // Refresh the main summary
        fetchReferencesSummary();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Assignment Failed',
          text: response.data.message || 'Failed to assign reference',
          confirmButtonColor: '#EF4444'
        });
      }
    } catch (error) {
      console.error('❌ Error assigning reference:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: error.response?.data?.message || 'Failed to assign reference',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setReferenceAssignmentLoading(false);
    }
  };

  // Open transfer modal
  const openTransferModal = (order) => {
    setTransferOrder(order);
    setTransferTargetRefId('');
    setShowTransferModal(true);
  };

  // Execute transfer
  const executeTransfer = async () => {
    if (!transferOrder || !transferTargetRefId) {
      await Swal.fire({ icon: 'warning', title: 'Select Target', text: 'Please select a new reference', confirmButtonColor: '#F59E0B' });
      return;
    }
    const targetRef = availableReferences.find(r => r._id === transferTargetRefId);
    const result = await Swal.fire({
      icon: 'question',
      title: 'Transfer Reference?',
      html: `Transfer DO <b>${transferOrder.doNumber}</b> from <b>${referenceDetails?.reference?.name}</b> to <b>${targetRef?.name || 'Unknown'}</b>?<br/><br/>` +
        `<span style="font-size:0.85em;color:#666">Commission &amp; receivable entries will be rebalanced automatically.</span>`,
      showCancelButton: true,
      confirmButtonText: 'Transfer',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#8B5CF6',
    });
    if (!result.isConfirmed) return;

    try {
      setTransferLoading(true);
      const resp = await api.put(`/transfer-reference/${transferOrder._id}`, {
        referenceId: transferTargetRefId,
        userId: user?.user?._id,
      });
      if (resp.data.success) {
        await Swal.fire({ icon: 'success', title: 'Transferred!', text: resp.data.message, confirmButtonColor: '#10B981' });
        setShowTransferModal(false);
        setTransferOrder(null);
        if (selectedReference) await fetchReferenceDetails(selectedReference._id);
        fetchReferencesSummary();
      } else {
        await Swal.fire({ icon: 'error', title: 'Transfer Failed', text: resp.data.message || 'Failed to transfer', confirmButtonColor: '#EF4444' });
      }
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Transfer failed', confirmButtonColor: '#EF4444' });
    } finally {
      setTransferLoading(false);
    }
  };

  // Remove reference from delivery order
  const removeReferenceFromOrder = async (order) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Remove Reference?',
      text: `Are you sure you want to remove the reference "${order.reference?.name}" from delivery order ${order.doNumber}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280'
    });

    if (!result.isConfirmed) return;

    try {
      setReferenceAssignmentLoading(true);

      const payload = {
        userId: user?.user?._id
      };

      console.log('🗑️ Removing reference from order:', order.doNumber);

      const response = await api.put(`/remove-reference/${order._id}`, payload);
      
      if (response.data.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Reference Removed!',
          text: response.data.message,
          confirmButtonColor: '#10B981'
        });

        // Refresh the reference details if we're viewing details
        if (selectedReference) {
          await fetchReferenceDetails(selectedReference._id);
        }
        // Refresh the main summary
        fetchReferencesSummary();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Removal Failed',
          text: response.data.message || 'Failed to remove reference',
          confirmButtonColor: '#EF4444'
        });
      }
    } catch (error) {
      console.error('❌ Error removing reference:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: error.response?.data?.message || 'Failed to remove reference',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setReferenceAssignmentLoading(false);
    }
  };

  // Open bulk reference assignment modal
  const openBulkReferenceAssignmentModal = async () => {
    console.log('🎯 Opening bulk reference assignment modal');
    
    // Fetch available references and orders without references
    await Promise.all([
      fetchAvailableReferences(),
      fetchDeliveryOrdersWithoutReferences()
    ]);
    
    setSelectedOrdersForBulkAssignment([]);
    setShowBulkReferenceModal(true);
  };

  // Handle bulk modal date range change
  const handleBulkModalDateChange = async (field, value) => {
    const newDateRange = { ...bulkModalDateRange, [field]: value };
    setBulkModalDateRange(newDateRange);
    
    // Fetch fresh data with new date range
    await fetchDeliveryOrdersWithoutReferences(newDateRange, 1, searchQuery);
    
    // Clear selections since data changed
    setSelectedOrdersForBulkAssignment([]);
  };

  // Close bulk reference assignment modal
  const closeBulkReferenceAssignmentModal = () => {
    setShowBulkReferenceModal(false);
    setSelectedOrdersForBulkAssignment([]);
    setSelectedReferenceId('');
    setSearchQuery('');
    setFilteredDeliveryOrders([]);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 50,
      hasNextPage: false,
      hasPrevPage: false
    });
  };

  // Prepare react-select options for references
  const referenceOptions = useMemo(() => {
    return availableReferences.map(ref => ({
      value: ref._id,
      label: `${ref.name}${ref.phoneNumber ? ` (${ref.phoneNumber})` : ''}`,
      data: ref
    }));
  }, [availableReferences]);

  // Handle react-select change
  const handleReferenceSelectChange = (selectedOption) => {
    setSelectedReferenceId(selectedOption ? selectedOption.value : '');
  };

  // Toggle order selection for bulk assignment
  const toggleOrderSelection = (order) => {
    setSelectedOrdersForBulkAssignment(prev => {
      const isSelected = prev.some(o => o._id === order._id);
      if (isSelected) {
        return prev.filter(o => o._id !== order._id);
      } else {
        return [...prev, order];
      }
    });
  };

  // Bulk assign reference to selected orders
  const bulkAssignReference = async () => {
    if (!selectedReferenceId || selectedOrdersForBulkAssignment.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a reference and at least one delivery order',
        confirmButtonColor: '#F59E0B'
      });
      return;
    }

    const selectedReference = availableReferences.find(ref => ref._id === selectedReferenceId);
    
    const result = await Swal.fire({
      icon: 'question',
      title: 'Bulk Assign Reference?',
      text: `Are you sure you want to assign "${selectedReference?.name}" to ${selectedOrdersForBulkAssignment.length} delivery orders?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, Assign',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280'
    });

    if (!result.isConfirmed) return;

    try {
      setBulkAssignmentLoading(true);

      const payload = {
        referenceId: selectedReferenceId,
        userId: user?.user?._id
      };

      console.log('🎯 Bulk assigning reference to orders:', selectedOrdersForBulkAssignment.map(o => o.doNumber));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process each order one by one
      for (const order of selectedOrdersForBulkAssignment) {
        try {
          const response = await api.put(`/assign-reference/${order._id}`, payload);
          if (response.data.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`${order.doNumber}: ${response.data.message}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`${order.doNumber}: ${error.response?.data?.message || 'Network error'}`);
        }
      }

      if (successCount > 0) {
        await Swal.fire({
          icon: successCount === selectedOrdersForBulkAssignment.length ? 'success' : 'warning',
          title: 'Bulk Assignment Complete!',
          html: `
            <div class="text-left">
              <p><strong>✅ Successfully assigned:</strong> ${successCount} orders</p>
              ${errorCount > 0 ? `<p><strong>❌ Failed:</strong> ${errorCount} orders</p>` : ''}
              ${errors.length > 0 ? `<div class="mt-2 text-sm text-gray-600"><strong>Errors:</strong><ul class="list-disc list-inside">${errors.map(err => `<li>${err}</li>`).join('')}</ul></div>` : ''}
            </div>
          `,
          confirmButtonColor: '#10B981'
        });

        // Close modal and refresh data
        closeBulkReferenceAssignmentModal();
        fetchReferencesSummary();
        fetchDeliveryOrdersWithoutReferences();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Assignment Failed',
          text: 'No orders were successfully assigned. Check the errors above.',
          confirmButtonColor: '#EF4444'
        });
      }

    } catch (error) {
      console.error('❌ Error in bulk assignment:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Network Error',
        text: 'Failed to process bulk assignment',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setBulkAssignmentLoading(false);
    }
  };

  // ─── Paid Commission Functions ──────────────────────────────────────────────

  const openPaidCommissionModal = (orderOrOrders) => {
    const orders = (Array.isArray(orderOrOrders) ? orderOrOrders : [orderOrOrders])
      .filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0);

    if (orders.length === 0) {
      Swal.fire('No Pending', 'Record payment is only available for delivery orders with pending commission.', 'info');
      return;
    }

    setPaidCommissionOrders(orders);
    const initialAmounts = {};
    orders.forEach(o => { initialAmounts[o._id] = ''; });
    setPaidCommissionForm({
      orderAmounts: initialAmounts,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentModes: [],
      remarks: '',
      claimReceivable: true,
    });
    setShowPaidCommissionModal(true);
  };

  const openBulkPaidCommissionModal = () => {
    if (!referenceDetails) return;
    const ordersWithCommission = referenceDetails.deliveryOrders.filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0);
    if (ordersWithCommission.length === 0) {
      Swal.fire('No Pending', 'All commissions are fully paid', 'info');
      return;
    }
    openPaidCommissionModal(ordersWithCommission);
  };

  const closePaidCommissionModal = () => {
    setShowPaidCommissionModal(false);
    setPaidCommissionOrders([]);
    setPaidCommissionForm({
      orderAmounts: {},
      paymentDate: new Date().toISOString().split('T')[0],
      paymentModes: [],
      remarks: '',
      claimReceivable: true,
    });
  };

  const togglePaymentMode = (mode) => {
    setPaidCommissionForm(prev => ({
      ...prev,
      paymentModes: prev.paymentModes.includes(mode)
        ? prev.paymentModes.filter(m => m !== mode)
        : [...prev.paymentModes, mode]
    }));
  };

  const updateOrderAmount = (orderId, value) => {
    setPaidCommissionForm(prev => ({
      ...prev,
      orderAmounts: { ...prev.orderAmounts, [orderId]: value }
    }));
  };

  const getBulkTotal = () => {
    return Object.values(paidCommissionForm.orderAmounts).reduce((sum, val) => sum + (Number(val) || 0), 0);
  };

  // Receivables are deducted at commission-assignment time, so the pending
  // entered here is paid in full as cash.
  const bulkTotal = getBulkTotal();
  const receivableOutstanding = referenceDetails?.receivableOutstanding || 0;

  const savePaidCommission = async () => {
    if (paidCommissionForm.paymentModes.length === 0) {
      Swal.fire({ title: 'Validation Error', text: 'Please select at least one payment mode', icon: 'warning', customClass: { container: '!z-[99999]' } });
      return;
    }
    if (paidCommissionOrders.length === 0) return;

    try {
      const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
      let orders = paidCommissionOrders
        .filter(o => Number(paidCommissionForm.orderAmounts[o._id]) > 0)
        .map(o => ({
          deliveryOrderId: o._id,
          doNumber: o.doNumber,
          totalCommission: o.totalTTAmount || 0,
          amountPaid: Number(paidCommissionForm.orderAmounts[o._id]),
        }));

      if (orders.length === 0) {
        Swal.fire({ title: 'No Amounts Entered', text: 'Please enter amount for at least one delivery order', icon: 'warning', customClass: { container: '!z-[99999]' } });
        return;
      }

      const total = r2(orders.reduce((s, o) => s + o.amountPaid, 0));
      const outstanding = r2(referenceDetails?.receivableOutstanding || 0);
      const claim = r2(Math.min(total, outstanding));
      const claimReceivable = paidCommissionForm.claimReceivable && claim > 0;

      // Receivable smaller than the payment → let the user choose (reference-level).
      if (claimReceivable && outstanding > 0 && outstanding < total) {
        const choice = await Swal.fire({
          title: 'Claim from receivable',
          html: `Reference owes <b>₹${outstanding.toFixed(2)}</b>.<br/>Settle it from this payment, then:<br/>` +
                `<b>Pay remaining now</b> → ₹${(total - outstanding).toFixed(2)} cash, commission fully paid.<br/>` +
                `<b>Leave partial</b> → ₹0 cash, ₹${(total - outstanding).toFixed(2)} pending (pay in another memo).`,
          icon: 'question',
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: 'Pay remaining now',
          denyButtonText: 'Leave partial',
          confirmButtonColor: '#059669',
          denyButtonColor: '#d97706',
          customClass: { container: '!z-[99999]' },
        });
        if (choice.isDismissed) return;
        if (choice.isDenied) {
          // Leave partial: total recorded = claim (receivable), allocate oldest-first.
          let rem = outstanding;
          orders = orders.map(o => {
            const amt = r2(Math.min(o.amountPaid, rem));
            rem = r2(rem - amt);
            return { ...o, amountPaid: amt };
          }).filter(o => o.amountPaid > 0);
        }
      }

      setPaidCommissionLoading(true);

      const payload = {
        referenceId: selectedReference?._id,
        orders,
        paymentDate: paidCommissionForm.paymentDate,
        paymentModes: paidCommissionForm.paymentModes,
        remarks: paidCommissionForm.remarks,
        userId: user?.user?._id,
        claimReceivable,
      };

      const response = await api.post('/paid-commission/bulk', payload);
      if (response.data.success) {
        closePaidCommissionModal();
        if (selectedReference) {
          await fetchReferenceDetails(selectedReference._id);
        }
        const memoResult = await Swal.fire({
          title: 'Success',
          text: response.data.message,
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Print Memo',
          cancelButtonText: 'Close',
          confirmButtonColor: '#059669',
          customClass: { container: '!z-[99999]' },
        });
        if (memoResult.isConfirmed) {
          printPaymentMemo(response.data.batchId);
        }
      }
    } catch (error) {
      console.error('Error saving paid commission:', error);
      Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to record payment', icon: 'error', customClass: { container: '!z-[99999]' } });
    } finally {
      setPaidCommissionLoading(false);
    }
  };

  const deletePaidCommission = async (recordId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Payment Record?',
      text: 'This action cannot be undone.',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
    });
    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/paid-commission/${recordId}`);
      if (response.data.success) {
        Swal.fire('Deleted', response.data.message, 'success');
        if (selectedReference) {
          await fetchReferenceDetails(selectedReference._id);
        }
      }
    } catch (error) {
      console.error('Error deleting paid commission:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to delete record', 'error');
    }
  };

  // Remove payment modal functions
  const openRemovePaymentModal = () => {
    if (!referenceDetails) return;
    const hasPayments = referenceDetails.deliveryOrders.some(o => o.paidCommissions && o.paidCommissions.length > 0);
    if (!hasPayments) {
      Swal.fire('No Payments', 'No payment records found to remove', 'info');
      return;
    }
    setSelectedPaymentIds([]);
    setRemovePaymentDateFrom('');
    setRemovePaymentDateTo('');
    setShowRemovePaymentModal(true);
  };

  const removeSelectedPayments = async () => {
    if (selectedPaymentIds.length === 0) {
      Swal.fire({ title: 'No Selection', text: 'Please select at least one payment to remove', icon: 'warning', customClass: { container: '!z-[99999]' } });
      return;
    }
    const confirm = await Swal.fire({
      icon: 'warning',
      title: `Remove ${selectedPaymentIds.length} Payment(s)?`,
      text: 'This will undo the payment and restore the pending commission amount. This cannot be undone.',
      showCancelButton: true,
      confirmButtonText: 'Yes, Remove',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      customClass: { container: '!z-[99999]' }
    });
    if (!confirm.isConfirmed) return;

    setRemovePaymentLoading(true);
    try {
      let successCount = 0;
      let failCount = 0;
      for (const id of selectedPaymentIds) {
        try {
          await api.delete(`/paid-commission/${id}`);
          successCount++;
        } catch {
          failCount++;
        }
      }
      setShowRemovePaymentModal(false);
      setSelectedPaymentIds([]);
      await Swal.fire('Done', `${successCount} payment(s) removed${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success');
      if (selectedReference) {
        await fetchReferenceDetails(selectedReference._id);
      }
    } catch (error) {
      Swal.fire({ title: 'Error', text: 'Failed to remove payments', icon: 'error', customClass: { container: '!z-[99999]' } });
    } finally {
      setRemovePaymentLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };
  
  // Sync filtered orders with server results
  useEffect(() => {
    setFilteredDeliveryOrders(deliveryOrdersWithoutReferences);
  }, [deliveryOrdersWithoutReferences]);

  // Server-side search with debounce
  useEffect(() => {
    if (!showBulkReferenceModal) return;
    const timer = setTimeout(() => {
      fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, 1, searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter references based on search query
  const paymentStatusSections = useMemo(() => {
    const counts = references.reduce((acc, reference) => {
      acc[getReferencePaymentState(reference)] += 1;
      if ((reference.receivableOutstanding || 0) > 0) acc['receivable'] += 1;
      return acc;
    }, { all: references.length, 'no-commission': 0, 'to-be-paid': 0, 'paid': 0, 'receivable': 0 });

    return [
      { value: 'all', label: 'All', count: counts.all },
      { value: 'no-commission', label: 'No Commission', count: counts['no-commission'] },
      { value: 'to-be-paid', label: 'To Be Paid', count: counts['to-be-paid'] },
      { value: 'paid', label: 'Paid', count: counts.paid },
      { value: 'receivable', label: 'Receivable', count: counts['receivable'] },
    ];
  }, [references]);

  const filteredReferences = useMemo(() => {
    let filtered = references;

    // Apply payment status filter
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter(reference => {
        if (paymentStatusFilter === 'receivable') return (reference.receivableOutstanding || 0) > 0;
        return getReferencePaymentState(reference) === paymentStatusFilter;
      });
    }

    if (!referenceSearchQuery.trim()) {
      return filtered;
    }

    const query = referenceSearchQuery.toLowerCase().trim();
    return filtered.filter(reference => {
      const name = reference.referenceName?.toLowerCase() || '';
      const phone = reference.referencePhone?.toLowerCase() || '';
      const email = reference.referenceEmail?.toLowerCase() || '';
      const orderCount = reference.deliveryOrderCount?.toString() || '';
      const orderValue = reference.totalOrderValue?.toString() || '';
      const commission = reference.totalCommission?.toString() || '';
      const receivable = reference.receivableOutstanding?.toString() || '';

      const doNumberMatch = reference.deliveryOrders?.some(order =>
        order.doNumber?.toLowerCase().includes(query)
      ) || false;

      return (
        name.includes(query) ||
        phone.includes(query) ||
        email.includes(query) ||
        orderCount.includes(query) ||
        orderValue.includes(query) ||
        commission.includes(query) ||
        receivable.includes(query) ||
        doNumberMatch
      );
    });
  }, [referenceSearchQuery, references, paymentStatusFilter]);

  // Initial load
  useEffect(() => {
    console.log('🔄 Initial load useEffect triggered');
    console.log('🏢 Company ID:', companyId);
    console.log('🚀 Calling fetchReferencesSummary from initial load');
    fetchReferencesSummary();
  }, [companyId]);

  // Handle date range change
  useEffect(() => {
    console.log('📅 Date range change useEffect triggered');
    console.log('📊 New date range:', selectedDateRange);
    console.log('🚀 Calling fetchReferencesSummary from date range change');
    fetchReferencesSummary();
  }, [selectedDateRange]);

  // Auto-refresh every 60s so DO status changes (cancel/reset) made elsewhere
  // reflect here well within the 2-minute SLA. Entry-form modals are left
  // untouched to avoid resetting data mid-entry.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchReferencesSummary();
      if (
        showDetailsModal &&
        selectedReference &&
        !showCommissionModal &&
        !showPaidCommissionModal &&
        !showGRPaymentModal &&
        !showRemovePaymentModal &&
        !showMemoIssuedModal
      ) {
        fetchReferenceDetails(selectedReference._id);
      }
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange, companyId, showDetailsModal, selectedReference, showCommissionModal, showPaidCommissionModal, showGRPaymentModal, showRemovePaymentModal, showMemoIssuedModal]);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
      {/* Compact Header Section */}
      <div className={`sticky top-0 z-10 backdrop-blur-lg ${
        isDarkMode ? 'bg-gray-900/95 border-b border-gray-700/50' : 'bg-white/95 border-b border-gray-200'
      } shadow-lg`}>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Top Row: Title + Badges + Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30' 
                  : 'bg-gradient-to-br from-purple-100 to-blue-100 border border-purple-200'
              }`}>
                <FaMoneyBillWave className={`text-lg ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Reference Commission
              </h1>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                  isDarkMode 
                    ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
                    : 'bg-green-50 border border-green-300 text-green-700'
                }`}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Completed
                </span>
                {user.user.isTT && (
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                    isDarkMode 
                      ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400' 
                      : 'bg-purple-50 border border-purple-300 text-purple-700'
                  }`}>
                    TT Access
                  </span>
                )}
              </div>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Range */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700' 
                  : 'bg-white border-gray-200'
              }`}>
                <FaCalendarAlt className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                <input
                  type="date"
                  value={selectedDateRange.startDate}
                  onChange={(e) => setSelectedDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className={`border-none outline-none text-sm ${
                    isDarkMode 
                      ? 'bg-gray-800 text-gray-100 [color-scheme:dark]' 
                      : 'bg-white text-gray-900'
                  }`}
                />
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
                <input
                  type="date"
                  value={selectedDateRange.endDate}
                  onChange={(e) => setSelectedDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className={`border-none outline-none text-sm ${
                    isDarkMode 
                      ? 'bg-gray-800 text-gray-100 [color-scheme:dark]' 
                      : 'bg-white text-gray-900'
                  }`}
                />
              </div>
              
              <button
                onClick={openBulkReferenceAssignmentModal}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-purple-600 to-purple-500 text-white hover:from-purple-500 hover:to-purple-400 disabled:opacity-50 transition-all shadow-md"
                title="Bulk assign references to delivery orders"
              >
                <FaUsers className="text-sm" />
                <span>Assign</span>
              </button>
              <button
                onClick={exportSummaryToExcel}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-400 disabled:opacity-50 transition-all shadow-md"
              >
                <FaFileExport className="text-sm" />
                <span>Export</span>
              </button>
              <button
                onClick={exportGoodsReturnsToExcel}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-amber-600 to-orange-500 text-white hover:from-amber-500 hover:to-orange-400 disabled:opacity-50 transition-all shadow-md"
                title="Export goods returns with auto-deduction & collection breakdown"
              >
                <FaFileExport className="text-sm" />
                <span>Export GR</span>
              </button>
              {user.user.isTT && (
                <button
                  onClick={exportIncorrectCommissions}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-red-600 to-orange-500 text-white hover:from-red-500 hover:to-orange-400 disabled:opacity-50 transition-all shadow-md"
                  title="Export incorrect commissions audit report"
                >
                  <FaExclamationTriangle className="text-sm" />
                  <span>TT Audit</span>
                </button>
              )}
              <button
                onClick={() => setShowHelpModal(true)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg font-medium text-sm border transition-all shadow-md ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-600 text-purple-300 hover:bg-gray-700'
                    : 'bg-white border-gray-200 text-purple-600 hover:bg-purple-50'
                }`}
                title="How Reference Commission works"
              >
                <FaQuestionCircle className="text-sm" />
                <span className="hidden sm:inline">Help</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">

      {/* Compact Stats Bar + Search + View Toggle */}
      <div className={`rounded-xl shadow-lg overflow-hidden border mb-4 ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className={`px-4 py-3 ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            {/* Stats */}
            <div className="flex flex-wrap items-center gap-3">
              <div className={`flex items-center gap-1.5 text-sm font-medium ${
                isDarkMode ? 'text-blue-400' : 'text-blue-600'
              }`}>
                <FaUsers className="text-xs" />
                <span>{summary.totalReferences}</span>
              </div>
              <div className={`flex items-center gap-1.5 text-sm font-medium ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`}>
                <FaMoneyBillWave className="text-xs" />
                <span>{formatCurrency(summary.totalCommission)}</span>
              </div>
              <div className={`flex items-center gap-1.5 text-sm font-medium ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`}>
                <span>Paid: {formatCurrency(summary.totalPaid)}</span>
              </div>
              <div className={`flex items-center gap-1.5 text-sm font-medium ${
                summary.totalCommission - summary.totalPaid > 0
                  ? isDarkMode ? 'text-orange-400' : 'text-orange-600'
                  : isDarkMode ? 'text-green-400' : 'text-green-600'
              }`}>
                <span>Pending: {formatCurrency(summary.totalCommission - summary.totalPaid)}</span>
              </div>
              {(summary.totalReceivable || 0) > 0 && (
                <div className={`flex items-center gap-1.5 text-sm font-medium ${
                  isDarkMode ? 'text-rose-400' : 'text-rose-600'
                }`} title="Amount owed back by references — claimed from commission payments or collected as cash">
                  <FaHandHoldingUsd className="text-xs" />
                  <span>Receivable: {formatCurrency(summary.totalReceivable)}</span>
                </div>
              )}
              <div className={`flex items-center gap-1.5 text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <FaCalendarAlt className="text-xs" />
                <span>{(() => {
                  const s = new Date(selectedDateRange.startDate);
                  const e = new Date(selectedDateRange.endDate);
                  const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
                  return `${fmt(s)} - ${fmt(e)}`;
                })()}</span>
              </div>
            </div>
            
            {/* Section + Search + View Toggle */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-2 w-full sm:w-auto">
              <div className={`grid grid-cols-2 sm:flex rounded-lg border overflow-hidden ${
                isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
              }`}>
                {paymentStatusSections.map(section => {
                  const isActive = paymentStatusFilter === section.value;
                  return (
                    <button
                      key={section.value}
                      type="button"
                      onClick={() => setPaymentStatusFilter(section.value)}
                      className={`px-3 py-2 text-xs sm:text-sm font-semibold transition-all border-r last:border-r-0 ${
                        isDarkMode ? 'border-gray-600' : 'border-gray-200'
                      } ${
                        isActive
                          ? (isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                          : (isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-50')
                      }`}
                    >
                      <span>{section.label}</span>
                      <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] ${
                        isActive
                          ? 'bg-white/20 text-white'
                          : (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-600')
                      }`}>{section.count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="relative flex-1 sm:w-64">
                <input
                  type="text"
                  placeholder="Search by name, DO number, phone..."
                  value={referenceSearchQuery}
                  onChange={(e) => setReferenceSearchQuery(e.target.value)}
                  className={`w-full pl-3 pr-8 py-2 rounded-lg border text-sm ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <FaSearch className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-400'
                }`} />
              </div>
              <div className={`flex rounded-lg border overflow-hidden ${
                isDarkMode ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 transition-all ${
                    viewMode === 'table'
                      ? (isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                      : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-500 hover:bg-gray-50')
                  }`}
                  title="Table View"
                >
                  <FaTable className="text-sm" />
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-2 transition-all ${
                    viewMode === 'card'
                      ? (isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                      : (isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-white text-gray-500 hover:bg-gray-50')
                  }`}
                  title="Card View"
                >
                  <FaTh className="text-sm" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* References Table */}
      {viewMode === 'table' ? (
      <div className={`rounded-xl shadow-lg overflow-hidden border ${
        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Reference
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Contact
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Orders
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Value
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Commission
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Paid
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-rose-300' : 'text-rose-700'
                }`} title="Amount owed back — claimed from commission payments or collected as cash">
                  Receivable
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  %
                </th>
                <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${
              isDarkMode 
                ? 'bg-gray-800 divide-gray-700' 
                : 'bg-white divide-gray-200'
            }`}>
              {loading ? (
                <tr>
                  <td colSpan="9" className={`px-4 py-12 text-center ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <div className="flex flex-col justify-center items-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-3 border-t-3 border-purple-600 mb-3"></div>
                      <span className="text-sm font-medium">Loading references...</span>
                    </div>
                  </td>
                </tr>
              ) : references.length === 0 ? (
                <tr>
                  <td colSpan="9" className={`px-4 py-12 text-center ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <p className="text-sm font-medium">No references found</p>
                    <p className="text-xs mt-1">Try adjusting your date range</p>
                  </td>
                </tr>
              ) : filteredReferences.length === 0 ? (
                <tr>
                  <td colSpan="9" className={`px-4 py-12 text-center ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <p className="text-sm font-medium">No matching references</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </td>
                </tr>
              ) : (
                filteredReferences.map((reference) => {
                  const commissionPercentage = reference.totalOrderValue > 0 
                    ? ((reference.totalCommission / reference.totalOrderValue) * 100).toFixed(1) 
                    : '0.0';
                  
                  return (
                    <tr 
                      key={reference._id}
                      className={`transition-all duration-150 ${
                        isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-600'
                          }`}>
                            {reference.referenceName?.charAt(0)?.toUpperCase() || 'N'}
                          </div>
                          <span className={`text-sm font-medium ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {reference.referenceName || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className={`text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {reference.referencePhone || 'N/A'}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {reference.referenceEmail || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isDarkMode 
                            ? 'bg-blue-900/30 text-blue-300' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {reference.deliveryOrderCount}
                        </span>
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {formatCurrency(reference.totalOrderValue)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${
                          reference.totalCommission > 0
                            ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                            : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          {formatCurrency(reference.totalCommission)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${
                          (reference.totalPaid || 0) > 0
                            ? (reference.pendingCommission <= 0
                              ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                              : (isDarkMode ? 'text-amber-400' : 'text-amber-600'))
                            : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          {formatCurrency(reference.totalPaid || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" title="Owed back by the reference (cancelled-DO overpayment / goods-return recovery) — claimed from commission payments or collected as cash">
                        {(reference.receivableOutstanding || 0) > 0 ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                            isDarkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-50 text-rose-700'
                          }`}>
                            {formatCurrency(reference.receivableOutstanding)}
                          </span>
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          parseFloat(commissionPercentage) > 0
                            ? (isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                            : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                        }`}>
                          {commissionPercentage}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => viewReferenceDetails(reference)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-blue-400 hover:bg-blue-500/20' 
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                            title={user.user.isTT ? "View & Assign" : "View Details"}
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => printCommissionStatement(reference._id, reference.referenceName)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-purple-400 hover:bg-purple-500/20' 
                                : 'text-purple-600 hover:bg-purple-50'
                            }`}
                            title="Print Commission Statement"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => printGRReport(reference._id)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-pink-400 hover:bg-pink-500/20' 
                                : 'text-pink-600 hover:bg-pink-50'
                            }`}
                            title="Print GR Commission Report"
                          >
                            <FaFileAlt className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openRecoveryModal(reference)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-amber-400 hover:bg-amber-500/20' 
                                : 'text-amber-600 hover:bg-amber-50'
                            }`}
                            title="Record GR Commission Collection"
                          >
                            <FaHandHoldingUsd className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => exportReferenceDetails(reference._id, reference.referenceName)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-green-400 hover:bg-green-500/20' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title="Export Excel"
                          >
                            <FaDownload className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleWhatsAppSend(reference)}
                            className={`p-1.5 rounded-lg transition-all ${
                              isDarkMode 
                                ? 'text-emerald-400 hover:bg-emerald-500/20' 
                                : 'text-emerald-600 hover:bg-emerald-50'
                            }`}
                            title="Send via WhatsApp"
                          >
                            <FaWhatsapp className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
      /* Card View */
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className={`col-span-full py-12 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="animate-spin rounded-full h-10 w-10 border-b-3 border-t-3 border-purple-600 mx-auto mb-3"></div>
            <span className="text-sm">Loading references...</span>
          </div>
        ) : filteredReferences.length === 0 ? (
          <div className={`col-span-full py-12 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p className="text-sm">No references found</p>
          </div>
        ) : (
          filteredReferences.map((reference) => {
            const commissionPercentage = reference.totalOrderValue > 0 
              ? ((reference.totalCommission / reference.totalOrderValue) * 100).toFixed(1) 
              : '0.0';
            
            return (
              <div 
                key={reference._id}
                className={`rounded-xl border p-4 transition-all hover:shadow-lg ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 hover:border-gray-600' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Card Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                    isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {reference.referenceName?.charAt(0)?.toUpperCase() || 'N'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {reference.referenceName || 'N/A'}
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {reference.referencePhone || 'N/A'}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                    parseFloat(commissionPercentage) > 0
                      ? (isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                      : (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')
                  }`}>
                    {commissionPercentage}%
                  </span>
                  {(reference.receivableOutstanding || 0) > 0 && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isDarkMode ? 'bg-rose-900/30 text-rose-300' : 'bg-rose-50 text-rose-700'
                    }`} title="GR must-collect owed back">
                      Owes {formatCurrency(reference.receivableOutstanding)}
                    </span>
                  )}
                </div>
                
                {/* Card Stats */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div className={`text-center p-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Orders</div>
                    <div className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{reference.deliveryOrderCount}</div>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Value</div>
                    <div className={`text-xs font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{formatCurrency(reference.totalOrderValue)}</div>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                  }`}>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Commission</div>
                    <div className={`text-xs font-bold ${
                      reference.totalCommission > 0
                        ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                        : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                    }`}>{formatCurrency(reference.totalCommission)}</div>
                  </div>
                  <div className={`text-center p-2 rounded-lg ${
                    (reference.receivableOutstanding || 0) > 0
                      ? (isDarkMode ? 'bg-rose-900/30' : 'bg-rose-50')
                      : (isDarkMode ? 'bg-gray-700' : 'bg-gray-50')
                  }`}>
                    <div className={`text-xs ${(reference.receivableOutstanding || 0) > 0 ? (isDarkMode ? 'text-rose-300' : 'text-rose-600') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>Receivable</div>
                    <div className={`text-xs font-bold ${
                      (reference.receivableOutstanding || 0) > 0
                        ? (isDarkMode ? 'text-rose-300' : 'text-rose-700')
                        : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                    }`}>{(reference.receivableOutstanding || 0) > 0 ? formatCurrency(reference.receivableOutstanding) : '—'}</div>
                  </div>
                </div>
                
                {/* Card Actions */}
                <div className={`flex items-center gap-1 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    onClick={() => viewReferenceDetails(reference)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-blue-400 hover:bg-blue-500/20' 
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                    title={user.user.isTT ? "View & Assign" : "View Details"}
                  >
                    <FaEye className="w-3 h-3" />
                    <span>View</span>
                  </button>
                  <button
                    onClick={() => printCommissionStatement(reference._id, reference.referenceName)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-purple-400 hover:bg-purple-500/20' 
                        : 'text-purple-600 hover:bg-purple-50'
                    }`}
                    title="Print Commission Statement"
                  >
                    <FaPrint className="w-3 h-3" />
                    <span>Print</span>
                  </button>
                  <button
                    onClick={() => printGRReport(reference._id)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-pink-400 hover:bg-pink-500/20' 
                        : 'text-pink-600 hover:bg-pink-50'
                    }`}
                    title="Print GR Commission Report"
                  >
                    <FaFileAlt className="w-3 h-3" />
                    <span>GR</span>
                  </button>
                  <button
                    onClick={() => openRecoveryModal(reference)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-amber-400 hover:bg-amber-500/20' 
                        : 'text-amber-600 hover:bg-amber-50'
                    }`}
                    title="Record GR Commission Collection"
                  >
                    <FaHandHoldingUsd className="w-3 h-3" />
                    <span>Collect</span>
                  </button>
                  <button
                    onClick={() => exportReferenceDetails(reference._id, reference.referenceName)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-green-400 hover:bg-green-500/20' 
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title="Export Excel"
                  >
                    <FaDownload className="w-3 h-3" />
                    <span>Export</span>
                  </button>
                  <button
                    onClick={() => handleWhatsAppSend(reference)}
                    className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isDarkMode 
                        ? 'text-emerald-400 hover:bg-emerald-500/20' 
                        : 'text-emerald-600 hover:bg-emerald-50'
                    }`}
                    title="Send via WhatsApp"
                  >
                    <FaWhatsapp className="w-3 h-3" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      </div> {/* End Main Content */}

      {/* Enhanced Reference Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center py-8">
          <div className={`relative mx-4 p-6 w-full max-w-5xl shadow-2xl rounded-2xl ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
              : 'bg-white border border-gray-200'
          }`}>
            {/* Modal Header */}
            <div className={`mb-5 pb-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg shrink-0 ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                      <FaUsers className={`text-xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className={`text-xl font-bold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {selectedReference?.referenceName}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Reference Commission Details
                      </p>
                    </div>
                  </div>
                  {user.user.isTT && referenceDetails && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
                        isDarkMode
                          ? 'bg-blue-900/30 border border-blue-700 text-blue-300'
                          : 'bg-blue-50 border border-blue-200 text-blue-700'
                      }`}>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        TT Assignment Enabled
                      </span>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        isDarkMode
                          ? 'bg-amber-900/30 text-amber-300 border border-amber-700'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {referenceDetails.deliveryOrders.filter(order => order.totalTTAmount === 0 && order.canAssignCommission).length} assignable
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className={`p-2 rounded-lg transition-all shrink-0 ${
                    isDarkMode
                      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Action toolbar */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={openMemoIssuedModal}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    isDarkMode
                      ? 'text-emerald-400 border-emerald-700 hover:bg-emerald-900/30'
                      : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                  }`}
                  title="View issued memos and print selected"
                >
                  <FaPrint className="w-3.5 h-3.5" /> Memo Issued
                </button>
                <button
                  onClick={openPaidReportModal}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    isDarkMode
                      ? 'text-green-400 border-green-700 hover:bg-green-900/30'
                      : 'text-green-700 border-green-300 hover:bg-green-50'
                  }`}
                  title="Select delivery orders for paid commission report"
                >
                  <FaPrint className="w-3.5 h-3.5" /> Paid Report
                </button>
                <button
                  onClick={openToBePaidReportModal}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    isDarkMode
                      ? 'text-amber-400 border-amber-700 hover:bg-amber-900/30'
                      : 'text-amber-700 border-amber-300 hover:bg-amber-50'
                  }`}
                  title="Select delivery orders for pending commission report"
                >
                  <FaPrint className="w-3.5 h-3.5" /> To Be Paid
                </button>
                <span className={`hidden sm:block w-px h-6 mx-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <button
                  onClick={openRemovePaymentModal}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    isDarkMode
                      ? 'text-red-400 border-red-700 hover:bg-red-900/30'
                      : 'text-red-600 border-red-300 hover:bg-red-50'
                  }`}
                  title="Remove payment records"
                >
                  <FaTrash className="w-3.5 h-3.5" /> Remove Payment
                </button>
                <button
                  onClick={() => handleWhatsAppSend(selectedReference, 'statement')}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm`}
                  title="Send via WhatsApp"
                >
                  <FaWhatsapp className="w-3.5 h-3.5" /> WhatsApp
                </button>
              </div>
            </div>

            {detailsLoading ? (
              <div className="text-center py-12">
                <div className={`animate-spin rounded-full h-12 w-12 mx-auto mb-4 ${
                  isDarkMode 
                    ? 'border-b-4 border-t-4 border-purple-500' 
                    : 'border-b-4 border-t-4 border-purple-600'
                }`}></div>
                <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Loading details...
                </p>
              </div>
            ) : referenceDetails ? (
              <div className="max-h-96 overflow-y-auto">
                {/* Enhanced Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                  <div className={`p-4 rounded-xl border text-center transition-all hover:shadow-lg ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-gray-750 to-gray-700 border-blue-700/50' 
                      : 'bg-gradient-to-br from-blue-50 to-white border-blue-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Orders
                    </p>
                    <p className={`text-2xl font-bold ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {referenceDetails.summary.totalOrders}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border text-center transition-all hover:shadow-lg ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-gray-750 to-gray-700 border-purple-700/50' 
                      : 'bg-gradient-to-br from-purple-50 to-white border-purple-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Order Value
                    </p>
                    <p className={`text-2xl font-bold ${
                      isDarkMode ? 'text-purple-400' : 'text-purple-600'
                    }`}>
                      {formatCurrency(referenceDetails.summary.totalOrderValue)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border text-center transition-all hover:shadow-lg ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-gray-750 to-gray-700 border-green-700/50' 
                      : 'bg-gradient-to-br from-green-50 to-white border-green-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Commission
                    </p>
                    <p className={`text-2xl font-bold ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>
                      {formatCurrency(referenceDetails.summary.totalCommission)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border text-center transition-all hover:shadow-lg ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-gray-750 to-gray-700 border-emerald-700/50' 
                      : 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Total Paid
                    </p>
                    <p className={`text-2xl font-bold ${
                      isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                    }`}>
                      {formatCurrency(referenceDetails.summary.totalPaid || 0)}
                    </p>
                  </div>
                  <div className={`p-4 rounded-xl border text-center transition-all hover:shadow-lg ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-gray-750 to-gray-700 border-amber-700/50' 
                      : 'bg-gradient-to-br from-amber-50 to-white border-amber-200'
                  }`}>
                    <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Pending
                    </p>
                    <p className={`text-2xl font-bold ${
                      isDarkMode ? 'text-amber-400' : 'text-amber-600'
                    }`}>
                      {formatCurrency((referenceDetails.summary.totalCommission || 0) - (referenceDetails.summary.totalPaid || 0))}
                    </p>
                  </div>
                </div>

                {/* Unified Receivable Banner */}
                {(referenceDetails.receivableOutstanding || 0) > 0 && (
                  <div className={`mb-4 p-4 rounded-xl border ${
                    isDarkMode
                      ? 'bg-rose-900/20 border-rose-700/50'
                      : 'bg-rose-50 border-rose-200'
                  }`}>
                    <h4 className={`text-sm font-bold mb-2 flex items-center gap-2 ${
                      isDarkMode ? 'text-rose-300' : 'text-rose-800'
                    }`}>
                      <FaHandHoldingUsd />
                      Receivable: {formatCurrency(referenceDetails.receivableOutstanding)}
                    </h4>
                    <p className={`text-xs mb-3 ${isDarkMode ? 'text-rose-400/80' : 'text-rose-700'}`}>
                      Amount owed back by this reference (cancelled-DO overpayments &amp; goods-return recovery). Claimed from commission payments, or collect cash below.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {(referenceDetails.receivables || []).map(rec => (
                        <span key={rec._id} className={`text-xs px-2.5 py-1 rounded-lg font-medium inline-flex items-center gap-1.5 ${
                          isDarkMode
                            ? 'bg-rose-500/20 text-rose-200 border border-rose-500/30'
                            : 'bg-white text-rose-800 border border-rose-300'
                        }`}>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                            rec.source === 'do-cancellation'
                              ? (isDarkMode ? 'bg-cyan-900/60 text-cyan-200' : 'bg-cyan-100 text-cyan-800')
                              : (isDarkMode ? 'bg-amber-900/60 text-amber-200' : 'bg-amber-100 text-amber-800')
                          }`}>
                            {rec.source === 'do-cancellation' ? 'Cancelled DO' : 'Goods Return'}
                          </span>
                          {rec.source === 'do-cancellation'
                            ? (rec.doNumber ? `DO ${rec.doNumber}` : 'Entry')
                            : (rec.returnId || 'GR')}
                          : {formatCurrency(rec.outstanding)}
                        </span>
                      ))}
                      <button
                        onClick={() => openRecoveryModal(selectedReference)}
                        className="text-xs px-3 py-1.5 rounded-lg font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-all"
                      >
                        Collect Cash
                      </button>
                    </div>
                  </div>
                )}

                {/* Cancelled DOs — excluded from commission, shown read-only */}
                {(referenceDetails.cancelledOrders || []).length > 0 && (
                  <div className={`mb-4 rounded-xl border overflow-hidden ${
                    isDarkMode ? 'border-gray-600/50 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <button
                      type="button"
                      onClick={() => setShowCancelledOrders(prev => !prev)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold ${
                        isDarkMode ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <FaTimes />
                        Cancelled Delivery Orders ({referenceDetails.cancelledOrders.length}) — excluded from commission
                      </span>
                      <span className="text-xs">{showCancelledOrders ? '▲ Hide' : '▼ Show'}</span>
                    </button>
                    {showCancelledOrders && (
                      <div className="overflow-x-auto border-t border-inherit">
                        <table className="min-w-full text-sm">
                          <thead className={isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100'}>
                            <tr>
                              {['DO Number', 'Cancelled On', 'Reason', 'Paid Before Cancellation', 'Receivable'].map(h => (
                                <th key={h} className={`px-3 py-2 text-left text-xs font-semibold uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                            {referenceDetails.cancelledOrders.map(o => (
                              <tr key={o._id}>
                                <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{o.doNumber}</td>
                                <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {o.cancelledAt ? new Date(o.cancelledAt).toLocaleDateString('en-IN') : '-'}
                                </td>
                                <td className={`px-3 py-2 max-w-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} title={o.cancelReason}>
                                  {o.cancelReason || '-'}
                                </td>
                                <td className={`px-3 py-2 ${(o.paidBeforeCancellation || 0) > 0 ? (isDarkMode ? 'text-emerald-300' : 'text-emerald-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                                  {(o.paidBeforeCancellation || 0) > 0 ? formatCurrency(o.paidBeforeCancellation) : 'Unpaid'}
                                </td>
                                <td className={`px-3 py-2 ${o.receivable && o.receivable.remaining > 0 ? (isDarkMode ? 'text-rose-300' : 'text-rose-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                                  {o.receivable && o.receivable.remaining > 0
                                    ? `${formatCurrency(o.receivable.remaining)} outstanding`
                                    : o.receivable
                                      ? `${formatCurrency(o.receivable.amount)} fully settled`
                                      : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Enhanced Quick Actions for TT Users */}
                {user.user.isTT && referenceDetails && (
                  <div className={`mb-6 p-4 rounded-xl border ${
                    isDarkMode 
                      ? 'bg-blue-900/20 border-blue-700/50' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <h4 className={`text-sm font-bold mb-3 flex items-center gap-2 ${
                      isDarkMode ? 'text-blue-300' : 'text-blue-800'
                    }`}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM9 9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-5a1 1 0 100 2 1 1 0 000-2z" />
                      </svg>
                      Commission Assignment Quick Actions
                    </h4>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className={`px-3 py-1.5 rounded-lg font-medium ${
                        isDarkMode 
                          ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                          : 'bg-blue-100 text-blue-700 border border-blue-200'
                      }`}>
                        📊 {referenceDetails.deliveryOrders.length} total
                      </span>
                      <span className={`px-3 py-1.5 rounded-lg font-medium ${
                        isDarkMode 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                          : 'bg-green-100 text-green-700 border border-green-200'
                      }`}>
                        ✅ {referenceDetails.deliveryOrders.filter(order => order.totalTTAmount > 0).length} with commission
                      </span>
                      <span className={`px-3 py-1.5 rounded-lg font-medium ${
                        isDarkMode 
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' 
                          : 'bg-amber-100 text-amber-700 border border-amber-200'
                      }`}>
                        ⏳ {referenceDetails.deliveryOrders.filter(order => order.totalTTAmount === 0 && order.canAssignCommission).length} assignable
                      </span>
                      <span className={`px-3 py-1.5 rounded-lg font-medium ${
                        isDarkMode 
                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' 
                          : 'bg-rose-100 text-rose-700 border border-rose-200'
                      }`}>
                        ↩ {referenceDetails.deliveryOrders.filter(order => order.commissionBlockedByReturn).length} fully returned
                      </span>
                      <span className={`px-3 py-1.5 rounded-lg font-medium ${
                        isDarkMode 
                          ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                          : 'bg-purple-100 text-purple-700 border border-purple-200'
                      }`}>
                        🔒 {referenceDetails.deliveryOrders.filter(order => !order.canAssignCommission).length} managed by TT
                      </span>
                      {referenceDetails.deliveryOrders.some(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0) && (
                        <button
                          onClick={openBulkPaidCommissionModal}
                          className={`px-3 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 ${
                            isDarkMode
                              ? 'bg-green-600 text-white hover:bg-green-700 border border-green-500'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          <FaMoneyBillWave className="w-3 h-3" />
                          Record Payment for All
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Enhanced Orders List - hide fully paid orders */}
                <div className="space-y-4">
                  {referenceDetails.deliveryOrders.filter(order => !((order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0)).map((order) => (
                    <div key={order.doNumber} className={`border rounded-xl p-5 transition-all ${
                      user.user.isTT 
                        ? (isDarkMode 
                            ? 'border-blue-600/50 bg-blue-900/10 hover:bg-blue-900/20 hover:border-blue-500' 
                            : 'border-blue-200 bg-blue-50/50 hover:bg-blue-50 hover:border-blue-300')
                        : (isDarkMode 
                            ? 'border-gray-600 bg-gray-750 hover:bg-gray-700 hover:border-gray-500' 
                            : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300')
                    }`}>
                      {/* Enhanced Commission Assignment Header */}
                      {user.user.isTT && order.canAssignCommission && (
                        <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 p-3 rounded-lg border ${
                          (order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0
                            ? (isDarkMode ? 'bg-gray-500/10 border-gray-500/30' : 'bg-gray-100 border-gray-300')
                            : (isDarkMode ? 'bg-blue-500/10 border-blue-500/30' : 'bg-blue-100 border-blue-300')
                        }`}>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${
                              (order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0
                                ? (isDarkMode ? 'bg-gray-500/20' : 'bg-gray-200')
                                : (isDarkMode ? 'bg-blue-500/20' : 'bg-blue-200')
                            }`}>
                              <FaEdit className={(order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0 ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : (isDarkMode ? 'text-blue-400' : 'text-blue-600')} />
                            </div>
                            <span className={`text-sm font-semibold ${
                              (order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0
                                ? (isDarkMode ? 'text-gray-400' : 'text-gray-600')
                                : (isDarkMode ? 'text-blue-300' : 'text-blue-800')
                            }`}>
                              {(order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0 ? 'Commission Fully Paid' : 'Commission Assignment Available'}
                            </span>
                          </div>
                          {!((order.pendingCommission || 0) <= 0 && (order.totalPaid || 0) > 0) && (
                          <button
                            onClick={() => openCommissionAssignmentModal(order)}
                            className={`text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 shadow-md ${
                              isDarkMode
                                ? 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-500'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            <FaEdit className="w-4 h-4" />
                            Assign Commission
                          </button>
                          )}
                        </div>
                      )}

                      {user.user.isTT && order.commissionBlockedByReturn && (
                        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 p-3 rounded-lg border ${
                          isDarkMode
                            ? 'bg-rose-500/10 border-rose-500/30'
                            : 'bg-rose-50 border-rose-300'
                        }`}>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${
                              isDarkMode ? 'bg-rose-500/20' : 'bg-rose-200'
                            }`}>
                              <FaExclamationTriangle className={isDarkMode ? 'text-rose-300' : 'text-rose-600'} />
                            </div>
                            <span className={`text-sm font-semibold ${
                              isDarkMode ? 'text-rose-300' : 'text-rose-800'
                            }`}>
                              Commission blocked by goods return
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
                              isDarkMode
                                ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                                : 'bg-rose-100 text-rose-700 border border-rose-200'
                            }`}>
                              {order.commissionBlockedReason || 'Fully returned'}
                            </span>
                            {order.canRemoveCommission && (
                              <button
                                onClick={() => removeCommissionForOrder(order)}
                                disabled={removeCommissionLoading}
                                className={`text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 shadow-md ${
                                  isDarkMode
                                    ? 'bg-red-600 text-white hover:bg-red-700 border border-red-500 disabled:opacity-50'
                                    : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
                                }`}
                              >
                                <FaTrash className="w-4 h-4" />
                                {removeCommissionLoading ? 'Removing...' : 'Remove Commission'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Enhanced info when commission assignment is not available */}
                      {user.user.isTT && !order.canAssignCommission && !order.commissionBlockedByReturn && (
                        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 p-3 rounded-lg border ${
                          isDarkMode
                            ? 'bg-green-500/10 border-green-500/30'
                            : 'bg-green-50 border-green-300'
                        }`}>
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${
                              isDarkMode ? 'bg-green-500/20' : 'bg-green-200'
                            }`}>
                              <svg className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <span className={`text-sm font-semibold ${
                              isDarkMode ? 'text-green-300' : 'text-green-800'
                            }`}>
                              Commission handled by original creator ({order.createdBy?.name})
                            </span>
                          </div>
                          <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
                            isDarkMode
                              ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                              : 'bg-green-100 text-green-600 border border-green-200'
                          }`}>
                            TT-enabled creator
                          </span>
                        </div>
                      )}

                      {/* Enhanced Reference Assignment Section */}
                      <div className={`mb-4 p-4 rounded-xl border ${
                        isDarkMode
                          ? 'bg-purple-900/10 border-purple-700/50'
                          : 'bg-purple-50 border-purple-200'
                      }`}>
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${
                              isDarkMode ? 'bg-purple-500/20' : 'bg-purple-200'
                            }`}>
                              <FaUsers className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} />
                            </div>
                            <span className={`text-sm font-semibold ${
                              isDarkMode ? 'text-purple-300' : 'text-purple-800'
                            }`}>
                              Reference: {referenceDetails?.reference?.name}
                            </span>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => openTransferModal(order)}
                              disabled={transferLoading}
                              className={`text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${
                                transferLoading
                                  ? 'bg-gray-400 cursor-not-allowed'
                                  : isDarkMode
                                    ? 'bg-violet-600 text-white hover:bg-violet-700 border border-violet-500'
                                    : 'bg-violet-600 text-white hover:bg-violet-700'
                              }`}
                            >
                              <FaExchangeAlt className="w-3 h-3" />
                              Transfer
                            </button>
                            {/* Only show Remove if no commission assigned */}
                            {(!order.totalTTAmount || order.totalTTAmount === 0) && (
                              <button
                                onClick={() => removeReferenceFromOrder(order)}
                                className={`text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${
                                  isDarkMode
                                    ? 'bg-red-600 text-white hover:bg-red-700 border border-red-500'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                                }`}
                                disabled={referenceAssignmentLoading}
                              >
                                <FaTimes className="w-3 h-3" />
                                Remove Reference
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Commission Payment section - show when pending or any payment history exists */}
                      {order.totalTTAmount > 0 && ((order.pendingCommission || 0) > 0 || (order.paidCommissions && order.paidCommissions.length > 0)) && (
                        <div className={`mb-4 p-4 rounded-xl border ${
                          isDarkMode
                            ? 'bg-green-900/10 border-green-700/50'
                            : 'bg-green-50 border-green-200'
                        }`}>
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
                            <h4 className={`text-sm font-bold flex items-center gap-2 ${
                              isDarkMode ? 'text-green-300' : 'text-green-800'
                            }`}>
                              <FaMoneyBillWave className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                              Commission Payment
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                (order.pendingCommission || 0) <= 0
                                  ? (isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700')
                                  : (isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700')
                              }`}>
                                {(order.pendingCommission || 0) <= 0 ? 'Fully Paid' : `₹${(order.pendingCommission || 0).toFixed(2)} pending`}
                              </span>
                            </h4>
                            {(order.pendingCommission || 0) > 0 && (
                            <button
                              onClick={() => openPaidCommissionModal(order)}
                              className={`text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1.5 ${
                                isDarkMode
                                  ? 'bg-green-600 text-white hover:bg-green-700 border border-green-500'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                            >
                              <FaMoneyBillWave className="w-3 h-3" />
                              Record Payment
                            </button>
                            )}

                          </div>
                          
                          {/* Summary row */}
                          <div className="flex flex-wrap gap-4 mb-2 text-sm">
                            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                              Earned: <strong>{formatCurrency(order.earnedCommission ?? order.totalTTAmount)}</strong>
                            </span>
                            {(order.commissionWithheld || 0) > 0 && (
                              <span className={isDarkMode ? 'text-rose-300' : 'text-rose-600'}>
                                Recovered (receivable): <strong>-{formatCurrency(order.commissionWithheld)}</strong>
                              </span>
                            )}
                            <span className={isDarkMode ? 'text-green-300' : 'text-green-700'}>
                              Paid: <strong>{formatCurrency(order.totalPaid || 0)}</strong>
                            </span>
                            {(order.pendingCommission || 0) > 0 && (
                              <span className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                                Pending: <strong>{formatCurrency(order.pendingCommission)}</strong>
                              </span>
                            )}
                          </div>

                          {/* Payment records */}
                          {order.paidCommissions && order.paidCommissions.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {order.paidCommissions.map((pc) => (
                                <div key={pc._id} className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-3 rounded-lg border ${
                                  isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
                                }`}>
                                  <div className="flex flex-wrap items-center gap-3 text-sm">
                                    <span className={`font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                                      {formatCurrency(pc.amountPaid)}
                                    </span>
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                      {new Date(pc.paymentDate).toLocaleDateString('en-IN')}
                                    </span>
                                    <div className="flex gap-1">
                                      {pc.paymentModes.map((mode) => (
                                        <span key={mode} className={`text-xs px-2 py-0.5 rounded-full ${
                                          isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                                        }`}>
                                          {mode}
                                        </span>
                                      ))}
                                    </div>
                                    {pc.remarks && (
                                      <span className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        "{pc.remarks}"
                                      </span>
                                    )}
                                    {pc.createdBy?.name && (
                                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        by {pc.createdBy.name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={() => printPaymentMemo(pc.batchId || pc._id)}
                                      className={`text-xs px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 ${
                                        isDarkMode
                                          ? 'text-green-400 hover:bg-green-500/20 border border-green-500/30'
                                          : 'text-green-600 hover:bg-green-50 border border-green-200'
                                      }`}
                                      title="Print payment memo"
                                    >
                                      <FaPrint className="w-3 h-3" /> Memo
                                    </button>
                                    <button
                                      onClick={() => deletePaidCommission(pc._id)}
                                      className={`text-xs px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 ${
                                        isDarkMode
                                          ? 'text-red-400 hover:bg-red-500/20 border border-red-500/30'
                                          : 'text-red-500 hover:bg-red-50 border border-red-200'
                                      }`}
                                      title="Delete payment record"
                                    >
                                      <FaTimes className="w-3 h-3" /> Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                        <div className="flex-1">
                          <h4 className={`font-bold text-xl mb-1 ${
                            isDarkMode ? 'text-gray-100' : 'text-gray-900'
                          }`}>
                            {order.doNumber}
                          </h4>
                          <div className="flex flex-col gap-1">
                            <p className={`text-sm flex items-center gap-2 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {(() => {
                                const date = new Date(order.createdAt);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                              })()}
                            </p>
                            <p className={`text-sm flex items-center gap-2 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              {order.recipient?.name || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className={`p-4 rounded-xl border ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            Order Value
                          </p>
                          <p className={`text-lg font-bold ${
                            isDarkMode ? 'text-purple-400' : 'text-purple-600'
                          }`}>
                            {formatCurrency(order.totalAmount)}
                          </p>
                          <p className={`text-xs font-medium uppercase tracking-wide mt-2 mb-1 ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            Commission
                          </p>
                          <p className={`text-lg font-bold ${
                            isDarkMode ? 'text-green-400' : 'text-green-600'
                          }`}>
                            {formatCurrency(order.earnedCommission ?? order.totalTTAmount)}
                            {(order.commissionWithheld || 0) > 0 && (
                              <span className={`block text-xs font-normal ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>
                                -{formatCurrency(order.commissionWithheld)} recovered from receivable
                              </span>
                            )}
                          </p>
                          
                          {/* Show creator info */}
                          <div className={`mt-3 pt-3 border-t ${
                            isDarkMode ? 'border-gray-600' : 'border-gray-300'
                          }`}>
                            <p className={`text-xs flex items-center gap-1 ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Created by: 
                              <span className="font-medium">{order.createdBy?.name || 'Unknown'}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                order.createdBy?.isTT
                                  ? (isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700')
                                  : (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                              }`}>
                                {order.createdBy?.isTT ? 'TT' : 'Non-TT'}
                              </span>
                            </p>
                          </div>

                          {/* Show appropriate action based on creator's TT status */}
                          {user.user.isTT && order.canRemoveCommission ? (
                            <button
                              onClick={() => removeCommissionForOrder(order)}
                              disabled={removeCommissionLoading}
                              className={`mt-3 w-full text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md ${
                                isDarkMode
                                  ? 'bg-red-600 text-white hover:bg-red-700 border border-red-500 disabled:opacity-50'
                                  : 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
                              }`}
                            >
                              <FaTrash className="w-4 h-4" />
                              {removeCommissionLoading ? 'Removing...' : 'Remove Commission'}
                            </button>
                          ) : user.user.isTT && order.canAssignCommission ? (
                            <button
                              onClick={() => openCommissionAssignmentModal(order)}
                              className={`mt-3 w-full text-sm px-4 py-2 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md ${
                                isDarkMode
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-500'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              <FaEdit className="w-4 h-4" />
                              Assign Commission
                            </button>
                          ) : user.user.isTT && order.commissionBlockedByReturn ? (
                            <div className={`mt-2 text-xs italic ${
                              isDarkMode ? 'text-rose-400' : 'text-rose-600'
                            }`}>
                              {order.commissionBlockedReason || 'Fully returned goods return makes net commission zero'}
                            </div>
                          ) : user.user.isTT && !order.canAssignCommission ? (
                            <div className={`mt-2 text-xs italic ${
                              isDarkMode ? 'text-green-400' : 'text-green-600'
                            }`}>
                              Commission assigned by TT creator
                            </div>
                          ) : (
                            <div className={`mt-2 text-xs italic ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              TT access required to assign commission
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Items */}
                      <div className="mt-3">
                        <h5 className={`text-sm font-medium mb-2 ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                          Items Commission:
                        </h5>
                        
                        {/* Show different messages based on commission status and assignment capability */}
                        {order.totalTTAmount === 0 && order.commissionBlockedByReturn ? (
                          <div className={`rounded-lg p-3 mb-2 border ${
                            isDarkMode 
                              ? 'bg-rose-500/10 border-rose-500/30' 
                              : 'bg-rose-50 border-rose-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-rose-400' : 'text-rose-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className={`text-sm font-medium ${
                                isDarkMode ? 'text-rose-300' : 'text-rose-800'
                              }`}>
                                Commission not applicable
                              </span>
                            </div>
                            <p className={`text-xs mt-1 ${
                              isDarkMode ? 'text-rose-400/80' : 'text-rose-700'
                            }`}>
                              {order.commissionBlockedReason || 'All ordered quantity has already been returned. Commission on net quantity is zero.'}
                            </p>
                          </div>
                        ) : order.totalTTAmount === 0 && user.user.isTT && order.canAssignCommission ? (
                          <div className={`rounded-lg p-3 mb-2 border ${
                            isDarkMode 
                              ? 'bg-amber-500/10 border-amber-500/30' 
                              : 'bg-amber-50 border-amber-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-amber-400' : 'text-amber-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className={`text-sm font-medium ${
                                isDarkMode ? 'text-amber-300' : 'text-amber-800'
                              }`}>
                                No commission assigned yet
                              </span>
                            </div>
                            <p className={`text-xs mt-1 ${
                              isDarkMode ? 'text-amber-400/80' : 'text-amber-700'
                            }`}>
                              This order was created by a non-TT employee. You can assign commission values.
                            </p>
                          </div>
                        ) : order.totalTTAmount === 0 && !order.canAssignCommission ? (
                          <div className={`rounded-lg p-2 mb-2 border ${
                            isDarkMode 
                              ? 'bg-blue-500/10 border-blue-500/30' 
                              : 'bg-blue-50 border-blue-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-blue-400' : 'text-blue-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className={`text-sm ${
                                isDarkMode ? 'text-blue-300' : 'text-blue-800'
                              }`}>
                                Commission managed by TT creator ({order.createdBy?.name})
                              </span>
                            </div>
                          </div>
                        ) : order.totalTTAmount === 0 ? (
                          <div className={`rounded-lg p-2 mb-2 border ${
                            isDarkMode 
                              ? 'bg-gray-700 border-gray-600' 
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <span className={`text-sm ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              No commission assigned
                            </span>
                          </div>
                        ) : null}
                        
                        <div className="space-y-1 text-sm">
                          {order.items.map((item, idx) => (
                            <div key={idx} className={`flex justify-between text-xs py-1 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                {item.name} ({item.quantity} {item.units})
                              </span>
                              <span className={`font-medium ${
                                item.ttCalculatedAmount > 0 
                                  ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                                  : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                              }`}>
                                {formatCurrency(item.ttCalculatedAmount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Failed to load details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commission Assignment Modal */}
      {showCommissionModal && editableCommissionData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl border ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Enhanced Modal Header */}
            <div className={`sticky top-0 z-10 px-6 py-4 border-b backdrop-blur-lg ${
              isDarkMode 
                ? 'bg-gradient-to-r from-blue-900/90 to-purple-900/90 border-gray-700/50' 
                : 'bg-gradient-to-r from-blue-600 to-purple-600 border-blue-700'
            }`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <FaMoneyBillWave className="text-white text-xl" />
                    </div>
                    Assign Commission
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    {editableCommissionData.doNumber}
                  </p>
                </div>
                <button
                  onClick={closeCommissionAssignmentModal}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all hover:rotate-90 duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              <div className="space-y-4">
                {editableCommissionData.items.map((item, index) => {
                  const amountBeforeDiscount = item.rate * item.quantity;
                  const discount = item.discount || 0;
                  const { totalItemValue, itemValueWithoutGST, calculatedAmount, isFixedCapped } = getCommissionMetrics(item);
                  
                  return (
                    <div key={index} className={`relative rounded-xl border transition-all hover:shadow-lg ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-gray-750 to-gray-800 border-gray-700/50 hover:border-blue-500/30' 
                        : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 hover:border-blue-300'
                    }`}>
                      {/* Decorative gradient */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/5 to-transparent rounded-bl-full overflow-hidden pointer-events-none"></div>
                      
                      <div className="relative p-5">
                        {/* Item Header */}
                        <div className="mb-4">
                          <div className="flex items-start gap-3 mb-2">
                            <div className={`p-2 rounded-lg ${
                              isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                            }`}>
                              <svg className={`w-5 h-5 ${
                                isDarkMode ? 'text-blue-400' : 'text-blue-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h4 className={`font-bold text-lg ${
                                isDarkMode ? 'text-gray-100' : 'text-gray-900'
                              }`}>
                                {item.name || 'N/A'}
                              </h4>
                              <div className={`flex flex-wrap gap-3 mt-2 text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                  {item.quantity} {item.units}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Rate: ₹{(totalItemValue / item.quantity).toFixed(2)}
                                </span>
                                {discount > 0 && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                                    {discount}% off
                                  </span>
                                )}
                                <span className={`font-semibold ${
                                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                                }`}>
                                  Total: ₹{totalItemValue.toFixed(2)}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDarkMode 
                                    ? 'bg-purple-500/20 text-purple-300' 
                                    : 'bg-purple-100 text-purple-700'
                                }`}>
                                  Without GST: ₹{itemValueWithoutGST.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* TT Commission Configuration */}
                        <div className={`commission-config rounded-xl p-4 border ${
                          isDarkMode 
                            ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30' 
                            : 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200'
                        }`}>
                          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
                            {/* Commission Input Section */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 min-w-0">
                              <div className="flex items-center gap-2 shrink-0">
                                <div className={`p-1.5 rounded-lg ${
                                  isDarkMode ? 'bg-blue-500/20' : 'bg-blue-600'
                                }`}>
                                  <FaMoneyBillWave className={`text-sm ${
                                    isDarkMode ? 'text-blue-400' : 'text-white'
                                  }`} />
                                </div>
                                <label className={`text-sm font-semibold whitespace-nowrap ${
                                  isDarkMode ? 'text-blue-300' : 'text-blue-900'
                                }`}>
                                  TT Commission:
                                </label>
                              </div>
                              
                              <div className="flex items-center gap-3 w-full sm:w-auto">
                                {/* Type Selector */}
                                <select
                                  value={item.tt.type}
                                  onChange={(e) => handleCommissionChange(index, 'tt.type', e.target.value)}
                                  onFocus={(e) => e.target.closest('.commission-config')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                                  className={`px-3 py-2 text-sm font-medium border rounded-lg transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none shrink-0 ${
                                    isDarkMode 
                                      ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                  style={{ minWidth: '140px' }}
                                >
                                  <option value="percentage">% Percentage</option>
                                  <option value="fixed">₹ Fixed Amount</option>
                                </select>
                                
                                {/* Value Input */}
                                <input
                                  type="number"
                                  min="0"
                                  step={item.tt.type === 'percentage' ? "0.01" : "1"}
                                  value={item.tt.value}
                                  onChange={(e) => handleCommissionChange(index, 'tt.value', e.target.value)}
                                  onFocus={(e) => e.target.closest('.commission-config')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })}
                                  className={`w-28 sm:w-32 px-3 py-2 text-sm font-medium border rounded-lg transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                                    isDarkMode 
                                      ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                      : 'bg-white border-gray-300 text-gray-900'
                                  }`}
                                  placeholder={item.tt.type === 'percentage' ? '10' : '1000'}
                                />
                              </div>
                            </div>
                            
                            {/* Calculated Amount Display */}
                            <div className={`text-right p-3 rounded-lg border shrink-0 ${
                              isDarkMode 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : 'bg-green-50 border-green-200'
                            }`}>
                              <div className={`text-xs font-medium mb-1 ${
                                isDarkMode ? 'text-green-300' : 'text-green-700'
                              }`}>
                                Commission Amount
                              </div>
                              <div className={`text-2xl font-bold ${
                                isDarkMode ? 'text-green-400' : 'text-green-600'
                              }`}>
                                ₹{calculatedAmount.toFixed(2)}
                              </div>
                              <div className={`text-xs mt-1 ${
                                isDarkMode ? 'text-green-400/70' : 'text-green-600'
                              }`}>
                                {item.tt.type === 'percentage' 
                                  ? `${item.tt.value}% of ₹${itemValueWithoutGST.toFixed(2)}`
                                  : (isFixedCapped
                                      ? `Fixed: ₹${Number(item.tt.value || 0).toFixed(2)} capped to ₹${calculatedAmount.toFixed(2)}`
                                      : `Fixed: ₹${Number(item.tt.value || 0).toFixed(2)}`)
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Enhanced Modal Footer */}
            <div className={`sticky bottom-0 px-6 py-4 border-t backdrop-blur-lg ${
              isDarkMode 
                ? 'bg-gray-800/95 border-gray-700/50' 
                : 'bg-gray-50/95 border-gray-200'
            }`}>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className={`text-center sm:text-left p-3 rounded-xl border ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30' 
                    : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                }`}>
                  <div className={`text-xs font-medium mb-1 ${
                    isDarkMode ? 'text-green-300' : 'text-green-700'
                  }`}>
                    Total Commission
                  </div>
                  <div className={`text-2xl font-bold ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`}>
                    ₹{editableCommissionData.items.reduce((sum, item) => sum + getCommissionMetrics(item).calculatedAmount, 0).toFixed(2)}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={closeCommissionAssignmentModal}
                    className={`px-6 py-2.5 rounded-xl font-medium transition-all hover:scale-105 active:scale-95 ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCommissionAssignments}
                    disabled={commissionAssignmentLoading}
                    className="px-6 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95 shadow-lg"
                  >
                    {commissionAssignmentLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save Commission'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Bulk Reference Assignment Modal */}
      {showBulkReferenceModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl border ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode 
                ? 'bg-gradient-to-r from-purple-900/40 to-purple-800/40 border-purple-700/50' 
                : 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200'
            }`}>
              <h3 className={`text-2xl font-bold flex items-center gap-3 ${
                isDarkMode ? 'text-purple-300' : 'text-purple-800'
              }`}>
                <div className={`p-2 rounded-xl ${
                  isDarkMode ? 'bg-purple-500/20' : 'bg-purple-500/10'
                }`}>
                  <FaUsers className={`text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                Bulk Reference Assignment
              </h3>
              <button
                onClick={closeBulkReferenceAssignmentModal}
                className={`p-2 rounded-lg transition-all ${
                  isDarkMode 
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 max-h-[calc(95vh-200px)] overflow-y-auto">
              {/* Date Range Section */}
              <div className={`mb-6 p-4 rounded-xl border ${
                isDarkMode 
                  ? 'bg-gray-750 border-gray-600' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className={`flex items-center gap-2 text-lg font-semibold mb-3 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  <FaCalendarAlt className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} />
                  Date Range for Orders:
                </label>
                <div className="flex gap-4 items-center">
                  <div className="flex-1">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>From Date:</label>
                    <input
                      type="date"
                      value={bulkModalDateRange.startDate}
                      onChange={(e) => handleBulkModalDateChange('startDate', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>To Date:</label>
                    <input
                      type="date"
                      value={bulkModalDateRange.endDate}
                      onChange={(e) => handleBulkModalDateChange('endDate', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Showing delivery orders from {(() => {
                    const startDate = new Date(bulkModalDateRange.startDate);
                    const endDate = new Date(bulkModalDateRange.endDate);
                    const formatDate = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                    return `${formatDate(startDate)} to ${formatDate(endDate)}`;
                  })()}
                </p>
              </div>

              {/* Reference Selection Section */}
              <div className="mb-6">
                <label className={`block text-lg font-semibold mb-3 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Select Reference to Assign:
                </label>
                <Select
                  value={referenceOptions.find(option => option.value === selectedReferenceId) || null}
                  onChange={handleReferenceSelectChange}
                  options={referenceOptions}
                  placeholder="-- Select a reference --"
                  isClearable
                  isSearchable
                  className="text-lg"
                  classNamePrefix="react-select"
                  styles={getSelectStyles(isDarkMode)}
                  formatOptionLabel={(option) => (
                    <div>
                      <div className="font-medium">{option.data.name}</div>
                      {option.data.phoneNumber && (
                        <div className="text-sm opacity-75">📞 {option.data.phoneNumber}</div>
                      )}
                      {option.data.classification && (
                        <div className="text-sm text-purple-500">🏷️ {option.data.classification}</div>
                      )}
                    </div>
                  )}
                />
                
                {availableReferences.length === 0 && (
                  <p className={`text-sm mt-2 ${
                    isDarkMode ? 'text-red-400' : 'text-red-500'
                  }`}>
                    No references available. Please add references first.
                  </p>
                )}
                
                {selectedReferenceId && (
                  <div className={`mt-3 p-3 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-purple-900/20 border-purple-700' 
                      : 'bg-purple-50 border-purple-200'
                  }`}>
                    {(() => {
                      const selectedRef = availableReferences.find(ref => ref._id === selectedReferenceId);
                      return selectedRef ? (
                        <div>
                          <h5 className={`font-semibold ${
                            isDarkMode ? 'text-purple-300' : 'text-purple-800'
                          }`}>
                            {selectedRef.name}
                          </h5>
                          {selectedRef.phoneNumber && (
                            <p className={`text-sm ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              📞 Phone: {selectedRef.phoneNumber}
                            </p>
                          )}
                          {selectedRef.emailAddress && (
                            <p className={`text-sm ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              📧 Email: {selectedRef.emailAddress}
                            </p>
                          )}
                          {selectedRef.classification && (
                            <p className={`text-sm ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              🏷️ Classification: {selectedRef.classification}
                            </p>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              {/* Delivery Orders Selection */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className={`text-lg font-semibold ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Select Completed Delivery Orders:
                  </label>
                  <div className={`text-sm flex items-center gap-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      isDarkMode 
                        ? 'bg-green-900/30 text-green-300' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      ✓ Completed Only
                    </span>
                    <span>
                      {selectedOrdersForBulkAssignment.length} of {filteredDeliveryOrders.length} selected
                      {searchQuery && ` (${deliveryOrdersWithoutReferences.length} total)`}
                    </span>
                    {loadingOrdersWithoutReferences && (
                      <span className="ml-2 text-purple-600">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                      </span>
                    )}
                  </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by DO Number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                    <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-400'
                    }`} />
                    {searchQuery && (
                      <button
                        onClick={() => { setSearchQuery(''); fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, 1, ''); }}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                          isDarkMode 
                            ? 'text-gray-400 hover:text-gray-200' 
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Showing {filteredDeliveryOrders.length} of {deliveryOrdersWithoutReferences.length} orders
                    </p>
                  )}
                </div>

                {loadingOrdersWithoutReferences ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mr-2"></div>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                      Loading completed delivery orders...
                    </span>
                  </div>
                ) : deliveryOrdersWithoutReferences.length === 0 ? (
                  <div className={`text-center py-8 px-4 rounded-lg ${
                    isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'
                  }`}>
                    <svg className="mx-auto h-12 w-12 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-lg font-medium mb-1">No Orders Found</p>
                    <p>No completed delivery orders found in this date range</p>
                  </div>
                ) : filteredDeliveryOrders.length === 0 ? (
                  <div className={`text-center py-8 px-4 rounded-lg ${
                    isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'
                  }`}>
                    <FaSearch className="mx-auto text-4xl mb-3 opacity-50" />
                    <p className="text-lg font-medium mb-1">No Results</p>
                    <p>No orders match your search: "{searchQuery}"</p>
                    <button
                      onClick={() => { setSearchQuery(''); fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, 1, ''); }}
                      className="mt-3 text-purple-600 hover:text-purple-700 underline"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className={`border rounded-lg overflow-hidden ${
                    isDarkMode ? 'border-gray-600' : 'border-gray-200'
                  }`}>
                    <div className="max-h-96 overflow-y-auto">
                      <table className={`min-w-full divide-y ${
                        isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
                      }`}>
                        <thead className={`sticky top-0 ${
                          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              <input
                                type="checkbox"
                                checked={selectedOrdersForBulkAssignment.length === filteredDeliveryOrders.length && filteredDeliveryOrders.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedOrdersForBulkAssignment([...filteredDeliveryOrders]);
                                  } else {
                                    setSelectedOrdersForBulkAssignment([]);
                                  }
                                }}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                title="Select all visible orders"
                              />
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              DO Number
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              Date
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              Recipient
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              Amount
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              Created By
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-500'
                            }`}>
                              Reference
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${
                          isDarkMode 
                            ? 'bg-gray-800 divide-gray-700' 
                            : 'bg-white divide-gray-200'
                        }`}>
                          {filteredDeliveryOrders.map((order) => {
                            const isSelected = selectedOrdersForBulkAssignment.some(o => o._id === order._id);
                            return (
                              <tr 
                                key={order._id} 
                                className={`cursor-pointer transition-colors ${
                                  isDarkMode 
                                    ? `hover:bg-gray-700 ${isSelected ? 'bg-purple-900/30' : 'bg-gray-800'}`
                                    : `hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : 'bg-white'}`
                                }`}
                                onClick={() => toggleOrderSelection(order)}
                              >
                                <td className="px-4 py-4 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleOrderSelection(order)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                </td>
                                <td className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                  {order.doNumber}
                                </td>
                                <td className={`px-4 py-4 whitespace-nowrap text-sm ${
                                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                                }`}>
                                  {(() => {
                                    const date = new Date(order.createdAt);
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const year = date.getFullYear();
                                    return `${day}/${month}/${year}`;
                                  })()}
                                </td>
                                <td className={`px-4 py-4 whitespace-nowrap text-sm ${
                                  isDarkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                  {order.recipient?.name || 'N/A'}
                                </td>
                                <td className={`px-4 py-4 whitespace-nowrap text-sm ${
                                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                                }`}>
                                  {formatCurrency(order.totalAmountWithGST)}
                                </td>
                                <td className={`px-4 py-4 whitespace-nowrap text-sm ${
                                  isDarkMode ? 'text-gray-300' : 'text-gray-500'
                                }`}>
                                  {order.createdBy?.name || 'N/A'}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm">
                                  {order.reference && typeof order.reference === 'object' && order.reference.name ? (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                      isDarkMode
                                        ? 'bg-amber-900/40 text-amber-300 border border-amber-700/50'
                                        : 'bg-amber-100 text-amber-800 border border-amber-200'
                                    }`}>
                                      🔗 {order.reference.name}
                                    </span>
                                  ) : (
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>None</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Enhanced Pagination Controls */}
                    {pagination.totalPages > 1 && (
                      <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t ${
                        isDarkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-gray-50'
                      }`}>
                        {/* Pagination Info */}
                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>
                            Showing <span className="font-semibold text-purple-500">{deliveryOrdersWithoutReferences.length}</span> of{' '}
                            <span className="font-semibold text-purple-500">{pagination.totalCount}</span> orders
                          </span>
                        </div>
                        
                        {/* Pagination Buttons */}
                        <div className="flex items-center gap-3">
                          {/* First Page Button */}
                          <button
                            onClick={() => fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, 1, searchQuery)}
                            disabled={pagination.currentPage === 1 || loadingOrdersWithoutReferences}
                            className={`p-2 rounded-lg transition-all ${
                              pagination.currentPage === 1 || loadingOrdersWithoutReferences
                                ? 'opacity-40 cursor-not-allowed'
                                : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm'
                            }`}
                            title="First Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                            </svg>
                          </button>
                          
                          {/* Previous Button */}
                          <button
                            onClick={() => fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, pagination.currentPage - 1, searchQuery)}
                            disabled={!pagination.hasPrevPage || loadingOrdersWithoutReferences}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                              !pagination.hasPrevPage || loadingOrdersWithoutReferences
                                ? 'opacity-40 cursor-not-allowed'
                                : isDarkMode
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            <span className="hidden sm:inline">Previous</span>
                          </button>
                          
                          {/* Page Numbers */}
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            isDarkMode 
                              ? 'bg-purple-900/30 border border-purple-700' 
                              : 'bg-purple-50 border border-purple-200'
                          }`}>
                            <span className={`text-sm font-medium ${
                              isDarkMode ? 'text-purple-300' : 'text-purple-700'
                            }`}>
                              Page
                            </span>
                            <span className={`text-lg font-bold ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              {pagination.currentPage}
                            </span>
                            <span className={`text-sm ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              of
                            </span>
                            <span className={`text-lg font-bold ${
                              isDarkMode ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              {pagination.totalPages}
                            </span>
                          </div>
                          
                          {/* Next Button */}
                          <button
                            onClick={() => fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, pagination.currentPage + 1, searchQuery)}
                            disabled={!pagination.hasNextPage || loadingOrdersWithoutReferences}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                              !pagination.hasNextPage || loadingOrdersWithoutReferences
                                ? 'opacity-40 cursor-not-allowed'
                                : isDarkMode
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm'
                            }`}
                          >
                            <span className="hidden sm:inline">Next</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          
                          {/* Last Page Button */}
                          <button
                            onClick={() => fetchDeliveryOrdersWithoutReferences(bulkModalDateRange, pagination.totalPages, searchQuery)}
                            disabled={pagination.currentPage === pagination.totalPages || loadingOrdersWithoutReferences}
                            className={`p-2 rounded-lg transition-all ${
                              pagination.currentPage === pagination.totalPages || loadingOrdersWithoutReferences
                                ? 'opacity-40 cursor-not-allowed'
                                : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300 shadow-sm'
                            }`}
                            title="Last Page"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Orders Summary */}
              {selectedOrdersForBulkAssignment.length > 0 && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-purple-900/20 border-purple-700' 
                    : 'bg-purple-50 border-purple-200'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    isDarkMode ? 'text-purple-300' : 'text-purple-800'
                  }`}>
                    Selected Orders Summary:
                  </h4>
                  <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 text-sm ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    <div>
                      <span className="font-medium">Total Orders:</span> {selectedOrdersForBulkAssignment.length}
                    </div>
                    <div>
                      <span className="font-medium">Total Value:</span> {formatCurrency(
                        selectedOrdersForBulkAssignment.reduce((sum, order) => sum + order.totalAmountWithGST, 0)
                      )}
                    </div>
                    <div>
                      <span className="font-medium">Date Range:</span> {
                        selectedOrdersForBulkAssignment.length > 0 ? 
                        (() => {
                          const minDate = new Date(Math.min(...selectedOrdersForBulkAssignment.map(o => new Date(o.createdAt))));
                          const maxDate = new Date(Math.max(...selectedOrdersForBulkAssignment.map(o => new Date(o.createdAt))));
                          const formatDate = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                          return `${formatDate(minDate)} - ${formatDate(maxDate)}`;
                        })() : 
                        'N/A'
                      }
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 p-6 border-t ${
              isDarkMode 
                ? 'bg-gray-750 border-gray-600' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium">
                  {selectedOrdersForBulkAssignment.length} orders selected
                </span>
                {selectedOrdersForBulkAssignment.length > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isDarkMode 
                      ? 'bg-purple-900/30 text-purple-300' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {formatCurrency(selectedOrdersForBulkAssignment.reduce((sum, order) => sum + order.totalAmountWithGST, 0))}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={closeBulkReferenceAssignmentModal}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                    isDarkMode 
                      ? 'bg-gray-600 text-gray-200 hover:bg-gray-500 border border-gray-500' 
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={bulkAssignReference}
                  disabled={bulkAssignmentLoading || !selectedReferenceId || selectedOrdersForBulkAssignment.length === 0}
                  className={`px-6 py-2.5 rounded-lg font-medium transition-all shadow-lg flex items-center gap-2 ${
                    bulkAssignmentLoading || !selectedReferenceId || selectedOrdersForBulkAssignment.length === 0
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 hover:shadow-xl'
                  }`}
                >
                  {bulkAssignmentLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Assigning...</span>
                    </>
                  ) : (
                    <>
                      <FaUsers className="w-4 h-4" />
                      <span>Assign to {selectedOrdersForBulkAssignment.length} Order{selectedOrdersForBulkAssignment.length !== 1 ? 's' : ''}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reference Assignment Modal */}
      {showReferenceAssignModal && selectedOrderForReference && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 to-gray-850 border-gray-700/50' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Enhanced Modal Header */}
            <div className={`sticky top-0 z-10 px-6 py-4 border-b backdrop-blur-lg ${
              isDarkMode 
                ? 'bg-gradient-to-r from-purple-900/90 to-pink-900/90 border-gray-700/50' 
                : 'bg-gradient-to-r from-purple-600 to-pink-600 border-purple-700'
            }`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <FaUsers className="text-white text-xl" />
                    </div>
                    Assign Reference
                  </h3>
                  <p className="text-purple-100 text-sm mt-1">
                    Link this order to a reference contact
                  </p>
                </div>
                <button
                  onClick={closeReferenceAssignmentModal}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all hover:rotate-90 duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {/* Order Info Card */}
              <div className={`relative overflow-hidden rounded-xl p-5 mb-6 border ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-gray-750 to-gray-800 border-gray-700/50' 
                  : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
              }`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-500/5 to-transparent rounded-bl-full"></div>
                
                <div className="relative">
                  <div className="flex items-start gap-4 mb-3">
                    <div className={`p-3 rounded-xl ${
                      isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                    }`}>
                      <svg className={`w-6 h-6 ${
                        isDarkMode ? 'text-purple-400' : 'text-purple-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-bold text-xl mb-1 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {selectedOrderForReference.doNumber}
                      </h4>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Delivery Order Details
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className={`p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <svg className={`w-4 h-4 ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Party
                        </span>
                      </div>
                      <p className={`text-sm font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {selectedOrderForReference.recipient?.name || 'N/A'}
                      </p>
                    </div>

                    <div className={`p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <svg className={`w-4 h-4 ${
                          isDarkMode ? 'text-green-400' : 'text-green-600'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Amount
                        </span>
                      </div>
                      <p className={`text-sm font-semibold ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`}>
                        {formatCurrency(selectedOrderForReference.totalAmount)}
                      </p>
                    </div>

                    <div className={`p-3 rounded-lg ${
                      isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <svg className={`w-4 h-4 ${
                          isDarkMode ? 'text-purple-400' : 'text-purple-600'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Date
                        </span>
                      </div>
                      <p className={`text-sm font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {(() => {
                          const date = new Date(selectedOrderForReference.createdAt);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </p>
                    </div>

                    {selectedOrderForReference.reference && (
                      <div className={`p-3 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-orange-500/10 border-orange-500/30' 
                          : 'bg-orange-50 border-orange-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <svg className={`w-4 h-4 ${
                            isDarkMode ? 'text-orange-400' : 'text-orange-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className={`text-xs font-medium ${
                            isDarkMode ? 'text-orange-300' : 'text-orange-700'
                          }`}>
                            Current Reference
                          </span>
                        </div>
                        <p className={`text-sm font-semibold ${
                          isDarkMode ? 'text-orange-200' : 'text-orange-900'
                        }`}>
                          {selectedOrderForReference.reference.name}
                        </p>
                        {selectedOrderForReference.reference.phoneNumber && (
                          <p className={`text-xs mt-0.5 ${
                            isDarkMode ? 'text-orange-300/70' : 'text-orange-600'
                          }`}>
                            {selectedOrderForReference.reference.phoneNumber}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Reference Selection Section */}
              <div className={`rounded-xl p-5 border mb-4 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30' 
                  : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'
              }`}>
                <label className={`block text-sm font-bold mb-3 flex items-center gap-2 ${
                  isDarkMode ? 'text-purple-300' : 'text-purple-900'
                }`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search & Select Reference:
                </label>
                
                <Select
                  value={referenceOptions.find(option => option.value === selectedReferenceId) || null}
                  onChange={handleReferenceSelectChange}
                  options={referenceOptions}
                  placeholder="🔍 Type to search by name or phone number..."
                  isClearable
                  isSearchable
                  classNamePrefix="react-select"
                  styles={getSelectStyles(isDarkMode)}
                  formatOptionLabel={(option) => (
                    <div className="py-1">
                      <div className="font-semibold text-base">{option.data.name}</div>
                      {option.data.phoneNumber && (
                        <div className={`text-sm flex items-center gap-1 mt-0.5 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {option.data.phoneNumber}
                        </div>
                      )}
                      {option.data.emailAddress && (
                        <div className={`text-xs flex items-center gap-1 mt-0.5 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {option.data.emailAddress}
                        </div>
                      )}
                    </div>
                  )}
                />
                
                {availableReferences.length === 0 && (
                  <div className={`mt-3 p-3 rounded-lg border flex items-start gap-2 ${
                    isDarkMode 
                      ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300' 
                      : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                  }`}>
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="font-medium">No references available</p>
                      <p className="text-xs mt-0.5 opacity-80">Please add references first before assigning.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Reference Preview */}
              {selectedReferenceId && (
                <div className={`rounded-xl p-5 border ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30' 
                    : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                }`}>
                  {(() => {
                    const selectedRef = availableReferences.find(ref => ref._id === selectedReferenceId);
                    return selectedRef ? (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`p-2 rounded-lg ${
                            isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
                          }`}>
                            <svg className={`w-5 h-5 ${
                              isDarkMode ? 'text-green-400' : 'text-green-600'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <span className={`text-sm font-bold ${
                            isDarkMode ? 'text-green-300' : 'text-green-700'
                          }`}>
                            Selected Reference
                          </span>
                        </div>
                        
                        <h5 className={`font-bold text-lg mb-2 ${
                          isDarkMode ? 'text-green-200' : 'text-green-900'
                        }`}>
                          {selectedRef.name}
                        </h5>
                        
                        <div className="space-y-2">
                          {selectedRef.phoneNumber && (
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-green-400' : 'text-green-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <p className={`text-sm ${
                                isDarkMode ? 'text-green-300' : 'text-green-700'
                              }`}>
                                {selectedRef.phoneNumber}
                              </p>
                            </div>
                          )}
                          {selectedRef.emailAddress && (
                            <div className="flex items-center gap-2">
                              <svg className={`w-4 h-4 ${
                                isDarkMode ? 'text-green-400' : 'text-green-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <p className={`text-sm ${
                                isDarkMode ? 'text-green-300' : 'text-green-700'
                              }`}>
                                {selectedRef.emailAddress}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>

            {/* Enhanced Modal Footer */}
            <div className={`sticky bottom-0 px-6 py-4 border-t backdrop-blur-lg ${
              isDarkMode 
                ? 'bg-gray-800/95 border-gray-700/50' 
                : 'bg-gray-50/95 border-gray-200'
            }`}>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeReferenceAssignmentModal}
                  className={`px-6 py-2.5 rounded-xl font-medium transition-all hover:scale-105 active:scale-95 ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={assignReferenceToOrder}
                  disabled={referenceAssignmentLoading || !selectedReferenceId}
                  className="px-6 py-2.5 rounded-xl font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95 shadow-lg"
                >
                  {referenceAssignmentLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {selectedOrderForReference.reference ? 'Changing...' : 'Assigning...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {selectedOrderForReference.reference ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Change Reference
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Assign Reference
                        </>
                      )}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paid Commission Modal */}
      {showPaidCommissionModal && paidCommissionOrders.length > 0 && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closePaidCommissionModal}></div>
          <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Record Commission Payment
                </h3>
                <button onClick={closePaidCommissionModal} className={`p-1 rounded-lg ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}>
                  <FaTimes />
                </button>
              </div>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {paidCommissionOrders.length === 1
                  ? `DO: ${paidCommissionOrders[0].doNumber} | Commission: ${formatCurrency(paidCommissionOrders[0].totalTTAmount)}`
                  : `${paidCommissionOrders.length} delivery orders selected`
                }
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Per-order amount inputs */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Delivery Order Wise Amount (₹) *
                </label>
                <div className={`rounded-lg border ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-200'
                }`}>
                  {paidCommissionOrders.map((o) => (
                    <div key={o._id} className={`px-3 py-2.5 border-b last:border-b-0 ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{o.doNumber}</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                            Comm: {formatCurrency(o.totalTTAmount || 0)}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            Pending: {formatCurrency(o.pendingCommission || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          value={paidCommissionForm.orderAmounts[o._id] || ''}
                          onChange={(e) => updateOrderAmount(o._id, e.target.value)}
                          className={`flex-1 px-3 py-1.5 rounded-lg border text-sm ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="Enter amount"
                        />
                        <label className="flex items-center gap-1 cursor-pointer select-none whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={Number(paidCommissionForm.orderAmounts[o._id]) === (o.pendingCommission || 0) && (o.pendingCommission || 0) > 0}
                            onChange={(e) => updateOrderAmount(o._id, e.target.checked ? String(o.pendingCommission || 0) : '')}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Full</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`mt-2 flex justify-between text-sm font-semibold ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <span>Cash Payable:</span>
                  <span className="text-green-600">{formatCurrency(bulkTotal)}</span>
                </div>
                {receivableOutstanding > 0 && (
                  <p className={`mt-1 text-xs ${isDarkMode ? 'text-rose-300' : 'text-rose-700'}`}>
                    Note: ₹{receivableOutstanding.toFixed(2)} receivable outstanding — it was already adjusted when commission was assigned, so this payment is the net payable.
                  </p>
                )}
              </div>

              {/* Date */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={paidCommissionForm.paymentDate}
                  onChange={(e) => setPaidCommissionForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {/* Payment Modes */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Payment Mode(s) *
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Cash', 'Bank Transfer', 'UPI', 'Cheque'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => togglePaymentMode(mode)}
                      className={`text-sm px-3 py-1.5 rounded-lg border transition-all ${
                        paidCommissionForm.paymentModes.includes(mode)
                          ? 'bg-green-600 text-white border-green-600'
                          : isDarkMode
                            ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-green-500'
                            : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {/* Claim from receivable (mandatory when a receivable exists) */}
              {receivableOutstanding > 0 && (
                <div className={`rounded-lg border p-3 ${isDarkMode ? 'bg-rose-900/20 border-rose-700/50' : 'bg-rose-50 border-rose-200'}`}>
                  <label className="flex items-center gap-2 select-none">
                    <input type="checkbox" checked disabled className="w-4 h-4 rounded accent-rose-600" />
                    <span className={`text-sm font-bold ${isDarkMode ? 'text-rose-200' : 'text-rose-800'}`}>CLAIM FROM RECEIVABLE</span>
                  </label>
                  <div className={`mt-2 grid grid-cols-3 gap-2 text-xs ${isDarkMode ? 'text-rose-200' : 'text-rose-800'}`}>
                    <div>Receivable<br /><strong>{formatCurrency(receivableOutstanding)}</strong></div>
                    <div>Claimed<br /><strong>{formatCurrency(Math.min(bulkTotal, receivableOutstanding))}</strong></div>
                    <div>Cash paid<br /><strong>{formatCurrency(Math.max(0, bulkTotal - Math.min(bulkTotal, receivableOutstanding)))}</strong></div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Remarks
                </label>
                <textarea
                  value={paidCommissionForm.remarks}
                  onChange={(e) => setPaidCommissionForm(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="Optional remarks..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={closePaidCommissionModal}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={savePaidCommission}
                disabled={paidCommissionLoading}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-all"
              >
                {paidCommissionLoading ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paid Report Selection Modal */}
      {showPaidReportModal && referenceDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPaidReportModal(false)}></div>
          <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Select DOs for Paid Report
                </h3>
                <button onClick={() => setShowPaidReportModal(false)} className={`p-1 rounded-lg ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}>
                  <FaTimes />
                </button>
              </div>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Select delivery orders with paid commissions to include in the report
              </p>
              {/* Show Inventory Toggle */}
              <label className={`flex items-center gap-2 mt-2 cursor-pointer`}>
                <input
                  type="checkbox"
                  checked={paidReportShowInventory}
                  onChange={(e) => setPaidReportShowInventory(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Include item-wise inventory breakdown
                </span>
              </label>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-2">
              <div className="flex justify-between items-center mb-3">
                <button
                  onClick={() => {
                    const paidOrders = referenceDetails.deliveryOrders.filter(o => o.paidCommissions && o.paidCommissions.length > 0);
                    setSelectedPaidReportOrders(prev => prev.length === paidOrders.length ? [] : paidOrders.map(o => o._id));
                  }}
                  className={`text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                >
                  {selectedPaidReportOrders.length === referenceDetails.deliveryOrders.filter(o => o.paidCommissions && o.paidCommissions.length > 0).length ? 'Deselect All' : 'Select All'}
                </button>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedPaidReportOrders.length} selected
                </span>
              </div>
              {referenceDetails.deliveryOrders.filter(o => o.paidCommissions && o.paidCommissions.length > 0).map(order => (
                <label key={order._id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedPaidReportOrders.includes(order._id)
                    ? (isDarkMode ? 'bg-green-900/20 border-green-600' : 'bg-green-50 border-green-300')
                    : (isDarkMode ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedPaidReportOrders.includes(order._id)}
                    onChange={() => setSelectedPaidReportOrders(prev =>
                      prev.includes(order._id) ? prev.filter(id => id !== order._id) : [...prev, order._id]
                    )}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{order.doNumber}</span>
                      <span className={`text-xs font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                        Paid: {formatCurrency(order.totalPaid || 0)}
                      </span>
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Commission: {formatCurrency(order.totalTTAmount || 0)} | {order.paidCommissions.length} payment(s)
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowPaidReportModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}>Cancel</button>
              <button
                onClick={printPaidReportFromModal}
                disabled={selectedPaidReportOrders.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print Paid Report ({selectedPaidReportOrders.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* To Be Paid Report Selection Modal */}
      {showToBePaidReportModal && referenceDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowToBePaidReportModal(false)}></div>
          <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Select DOs for To Be Paid Report
                </h3>
                <button onClick={() => setShowToBePaidReportModal(false)} className={`p-1 rounded-lg ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}>
                  <FaTimes />
                </button>
              </div>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Select delivery orders with pending commission to include in the report
              </p>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-2">
              <div className="flex justify-between items-center mb-3">
                <button
                  onClick={() => {
                    const pendingOrders = referenceDetails.deliveryOrders.filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0);
                    setSelectedToBePaidOrders(prev => prev.length === pendingOrders.length ? [] : pendingOrders.map(o => o._id));
                  }}
                  className={`text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                >
                  {selectedToBePaidOrders.length === referenceDetails.deliveryOrders.filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0).length ? 'Deselect All' : 'Select All'}
                </button>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {selectedToBePaidOrders.length} selected
                </span>
              </div>
              {referenceDetails.deliveryOrders.filter(o => (o.totalTTAmount || 0) > 0 && (o.pendingCommission || 0) > 0).map(order => (
                <label key={order._id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedToBePaidOrders.includes(order._id)
                    ? (isDarkMode ? 'bg-amber-900/20 border-amber-600' : 'bg-amber-50 border-amber-300')
                    : (isDarkMode ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedToBePaidOrders.includes(order._id)}
                    onChange={() => setSelectedToBePaidOrders(prev =>
                      prev.includes(order._id) ? prev.filter(id => id !== order._id) : [...prev, order._id]
                    )}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{order.doNumber}</span>
                      <span className={`text-xs font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                        Pending: {formatCurrency(order.pendingCommission || 0)}
                      </span>
                    </div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Commission: {formatCurrency(order.totalTTAmount || 0)} | Paid: {formatCurrency(order.totalPaid || 0)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowToBePaidReportModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}>Cancel</button>
              <button
                onClick={printToBePaidReportFromModal}
                disabled={selectedToBePaidOrders.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print To Be Paid ({selectedToBePaidOrders.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Payment Modal */}
      {/* Memo Issued Modal */}
      {showMemoIssuedModal && referenceDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowMemoIssuedModal(false)}></div>
          <div className={`relative w-full max-w-2xl rounded-2xl shadow-2xl border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Memo Issued
                </h3>
                <button onClick={() => setShowMemoIssuedModal(false)} className={`p-1 rounded-lg ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}>
                  <FaTimes />
                </button>
              </div>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Issued payment memos for this reference. Select one or more to print.
              </p>
              <div className="flex gap-3 mt-3">
                <div className="flex-1">
                  <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From</label>
                  <input
                    type="date"
                    value={memoIssuedDateFrom}
                    onChange={e => { setMemoIssuedDateFrom(e.target.value); setSelectedMemoKeys([]); }}
                    className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To</label>
                  <input
                    type="date"
                    value={memoIssuedDateTo}
                    onChange={e => { setMemoIssuedDateTo(e.target.value); setSelectedMemoKeys([]); }}
                    className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                    }`}
                  />
                </div>
                {(memoIssuedDateFrom || memoIssuedDateTo) && (
                  <button
                    onClick={() => { setMemoIssuedDateFrom(''); setMemoIssuedDateTo(''); setSelectedMemoKeys([]); }}
                    className={`self-end px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >Clear</button>
                )}
              </div>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-2">
              {memoIssuedLoading ? (
                <div className="text-center py-8">
                  <div className={`animate-spin rounded-full h-8 w-8 mx-auto mb-3 border-b-2 border-t-2 ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading memos...</p>
                </div>
              ) : memoIssuedList.length === 0 ? (
                <p className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No memos issued in this date range.
                </p>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-3">
                    <button
                      onClick={() => setSelectedMemoKeys(prev => prev.length === memoIssuedList.length ? [] : memoIssuedList.map(m => m.memoNumber))}
                      className={`text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                      {selectedMemoKeys.length === memoIssuedList.length ? 'Deselect All' : 'Select All'}
                    </button>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {selectedMemoKeys.length} selected
                    </span>
                  </div>
                  {memoIssuedList.map(memo => (
                    <label key={memo.memoNumber} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedMemoKeys.includes(memo.memoNumber)
                        ? (isDarkMode ? 'bg-emerald-900/20 border-emerald-600' : 'bg-emerald-50 border-emerald-300')
                        : (isDarkMode ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedMemoKeys.includes(memo.memoNumber)}
                        onChange={() => setSelectedMemoKeys(prev =>
                          prev.includes(memo.memoNumber) ? prev.filter(k => k !== memo.memoNumber) : [...prev, memo.memoNumber]
                        )}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <span className={`text-sm font-medium font-mono ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{memo.memoNumber}</span>
                          <span className={`text-xs font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            {memo.totals?.formattedAmountPaid || ''}
                          </span>
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {memo.payment?.formattedDate || 'No date'}
                          {' • '}{memo.totals?.count || 0} DO{(memo.totals?.count || 0) === 1 ? '' : 's'}
                          {memo.orders?.length ? ` • ${memo.orders.map(o => o.doNumber).join(', ')}` : ''}
                        </div>
                      </div>
                    </label>
                  ))}
                </>
              )}
            </div>
            <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setShowMemoIssuedModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}>Cancel</button>
              <button
                onClick={printSelectedMemos}
                disabled={selectedMemoKeys.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print Selected ({selectedMemoKeys.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemovePaymentModal && referenceDetails && (() => {
        const allPaymentsRaw = referenceDetails.deliveryOrders.flatMap(order =>
          (order.paidCommissions || []).map(pc => ({ ...pc, doNumber: order.doNumber }))
        );
        const allPayments = allPaymentsRaw.filter(p => {
          if (!removePaymentDateFrom && !removePaymentDateTo) return true;
          const pDate = p.paymentDate ? new Date(p.paymentDate).toISOString().slice(0, 10) : null;
          if (!pDate) return true;
          if (removePaymentDateFrom && pDate < removePaymentDateFrom) return false;
          if (removePaymentDateTo && pDate > removePaymentDateTo) return false;
          return true;
        });
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowRemovePaymentModal(false)}></div>
            <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Remove Payment Records
                  </h3>
                  <button onClick={() => setShowRemovePaymentModal(false)} className={`p-1 rounded-lg ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}>
                    <FaTimes />
                  </button>
                </div>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Select payment records to remove. This will restore the pending commission amount.
                </p>
                <div className="flex gap-3 mt-3">
                  <div className="flex-1">
                    <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From</label>
                    <input
                      type="date"
                      value={removePaymentDateFrom}
                      onChange={e => { setRemovePaymentDateFrom(e.target.value); setSelectedPaymentIds([]); }}
                      className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border text-sm ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To</label>
                    <input
                      type="date"
                      value={removePaymentDateTo}
                      onChange={e => { setRemovePaymentDateTo(e.target.value); setSelectedPaymentIds([]); }}
                      className={`w-full mt-0.5 px-2.5 py-1.5 rounded-lg border text-sm ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                      }`}
                    />
                  </div>
                  {(removePaymentDateFrom || removePaymentDateTo) && (
                    <button
                      onClick={() => { setRemovePaymentDateFrom(''); setRemovePaymentDateTo(''); setSelectedPaymentIds([]); }}
                      className={`self-end px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                        isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >Clear</button>
                  )}
                </div>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-2">
                <div className="flex justify-between items-center mb-3">
                  <button
                    onClick={() => {
                      setSelectedPaymentIds(prev => prev.length === allPayments.length ? [] : allPayments.map(p => p._id));
                    }}
                    className={`text-sm font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                  >
                    {selectedPaymentIds.length === allPayments.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedPaymentIds.length} selected
                  </span>
                </div>
                {allPayments.map(payment => (
                  <label key={payment._id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedPaymentIds.includes(payment._id)
                      ? (isDarkMode ? 'bg-red-900/20 border-red-600' : 'bg-red-50 border-red-300')
                      : (isDarkMode ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300')
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedPaymentIds.includes(payment._id)}
                      onChange={() => setSelectedPaymentIds(prev =>
                        prev.includes(payment._id) ? prev.filter(id => id !== payment._id) : [...prev, payment._id]
                      )}
                      className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{payment.doNumber}</span>
                        <span className={`text-xs font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {formatCurrency(payment.amountPaid || 0)}
                        </span>
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('en-IN') : 'No date'}
                        {payment.paymentModes?.length ? ` • ${payment.paymentModes.join(', ')}` : ''}
                        {payment.remarks ? ` • ${payment.remarks}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button onClick={() => setShowRemovePaymentModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>Cancel</button>
                <button
                  onClick={removeSelectedPayments}
                  disabled={selectedPaymentIds.length === 0 || removePaymentLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                  {removePaymentLoading ? 'Removing...' : `Remove Selected (${selectedPaymentIds.length})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    {/* ===== Receivable Recovery (Record Cash) Modal ===== */}
    {showGRPaymentModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className={`w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-rose-600 to-red-500 rounded-t-2xl">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FaHandHoldingUsd className="w-5 h-5" />
                Record Recovery (Cash)
              </h2>
              <p className="text-xs text-rose-100 mt-0.5">
                {grPaymentReference?.referenceName} — collect receivable amounts directly by cash
              </p>
            </div>
            <button onClick={() => setShowGRPaymentModal(false)} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-all">
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <p className={`text-xs px-3 py-2 rounded-lg border ${
              isDarkMode ? 'bg-gray-800/60 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
            }`}>
              These are amounts owed back by the reference (cancelled-DO overpayments &amp; goods-return recovery). They are claimed from commission payments, or collected as cash here.
            </p>
            {grPaymentLoading ? (
              <div className="flex items-center justify-center h-32">
                <svg className="animate-spin h-8 w-8 text-rose-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              </div>
            ) : recoveryEntries.length === 0 ? (
              <div className={`flex flex-col items-center justify-center h-40 gap-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <FaHandHoldingUsd className="w-10 h-10 text-gray-400" />
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No open receivables for this reference.
                </p>
                <p className="text-xs text-gray-400">Everything has been fully recovered or adjusted.</p>
              </div>
            ) : (
              recoveryEntries.map(entry => {
                const form = grPaymentForms[entry._id] || { amount: '', date: new Date().toISOString().split('T')[0], modes: [], remarks: '' };
                const maxAmount = Math.max(0, entry.outstanding);
                return (
                  <div key={entry._id} className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold uppercase ${
                        entry.source === 'do-cancellation'
                          ? (isDarkMode ? 'bg-cyan-900 text-cyan-300' : 'bg-cyan-100 text-cyan-700')
                          : (isDarkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-100 text-amber-700')
                      }`}>
                        {entry.source === 'do-cancellation' ? 'Cancelled DO' : 'Goods Return'}
                      </span>
                      {entry.source === 'gr-must-collect' && entry.returnId && (
                        <span className={`font-bold text-sm ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}>{entry.returnId}</span>
                      )}
                      {entry.doNumber && (
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>DO: {entry.doNumber}</span>
                      )}
                      <span className={`ml-auto text-sm font-bold ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                        Outstanding: ₹{entry.outstanding.toFixed(2)}
                      </span>
                    </div>

                    {/* Payment form */}
                    <div className={`mt-2 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-rose-50/70'}`}>
                      <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-rose-300' : 'text-rose-700'}`}>Record Cash Recovery (max ₹{maxAmount.toFixed(2)})</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Amount Received (₹)</label>
                          <input
                            type="number" min="0" step="0.01" max={maxAmount}
                            value={form.amount}
                            onChange={e => setGRPaymentForms(prev => ({ ...prev, [entry._id]: { ...prev[entry._id], amount: e.target.value } }))}
                            className={`w-full px-3 py-1.5 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Date</label>
                          <input
                            type="date"
                            value={form.date}
                            onChange={e => setGRPaymentForms(prev => ({ ...prev, [entry._id]: { ...prev[entry._id], date: e.target.value } }))}
                            className={`w-full px-3 py-1.5 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Remarks</label>
                          <input
                            type="text"
                            value={form.remarks}
                            onChange={e => setGRPaymentForms(prev => ({ ...prev, [entry._id]: { ...prev[entry._id], remarks: e.target.value } }))}
                            className={`w-full px-3 py-1.5 text-sm rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-rose-500`}
                            placeholder="Optional note"
                          />
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Payment Mode *</label>
                        <div className="flex flex-wrap gap-2">
                          {PAYMENT_MODES.map(mode => (
                            <label key={mode} className={`flex items-center gap-1.5 text-xs cursor-pointer px-2.5 py-1 rounded-full border transition-all ${
                              form.modes.includes(mode)
                                ? 'bg-rose-500 text-white border-rose-500'
                                : (isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600')
                            }`}>
                              <input
                                type="checkbox"
                                className="hidden"
                                checked={form.modes.includes(mode)}
                                onChange={e => setGRPaymentForms(prev => ({
                                  ...prev,
                                  [entry._id]: {
                                    ...prev[entry._id],
                                    modes: e.target.checked
                                      ? [...(prev[entry._id]?.modes || []), mode]
                                      : (prev[entry._id]?.modes || []).filter(m => m !== mode)
                                  }
                                }))}
                              />
                              {mode}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => saveRecoveryCash(entry._id)}
                          disabled={maxAmount <= 0}
                          className="px-5 py-1.5 rounded-lg text-sm font-semibold text-white bg-rose-500 hover:bg-rose-600 transition-all shadow disabled:opacity-50"
                        >
                          Save Recovery
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className={`px-6 py-3 border-t flex justify-end ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setShowGRPaymentModal(false)}
              className={`px-5 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    {/* ===== Transfer Reference Modal ===== */}
    {showTransferModal && transferOrder && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowTransferModal(false); setTransferOrder(null); }}></div>
        <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              <FaExchangeAlt className="text-violet-500" /> Transfer Reference
            </h3>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Move DO <strong>{transferOrder.doNumber}</strong> from <strong>{referenceDetails?.reference?.name}</strong> to a new reference.
            </p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                New Reference
              </label>
              <select
                value={transferTargetRefId}
                onChange={e => setTransferTargetRefId(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Select reference...</option>
                {availableReferences
                  .filter(r => r._id !== (referenceDetails?.reference?._id || ''))
                  .map(r => (
                    <option key={r._id} value={r._id}>{r.name}{r.phoneNumber ? ` (${r.phoneNumber})` : ''}</option>
                  ))
                }
              </select>
            </div>
            <div className={`text-xs rounded-lg p-3 ${isDarkMode ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-50 text-violet-700'}`}>
              <p className="font-semibold mb-1">What happens:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Paid commission becomes a receivable owed by the old reference</li>
                <li>Goods-return debts move to the new reference</li>
                <li>New reference&rsquo;s outstanding is claimed when its commission is paid</li>
              </ul>
            </div>
          </div>
          <div className={`px-6 py-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => { setShowTransferModal(false); setTransferOrder(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={executeTransfer}
              disabled={!transferTargetRefId || transferLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all ${
                !transferTargetRefId || transferLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 hover:bg-violet-700'
              }`}
            >
              {transferLoading ? 'Transferring...' : 'Transfer DO'}
            </button>
          </div>
        </div>
      </div>
    )}
    {/* ===== Help / Logic Explanation Modal ===== */}
    {showHelpModal && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowHelpModal(false)}></div>
        <div className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/30 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FaQuestionCircle className="w-5 h-5" />
                How Reference Commission Works
              </h2>
              <p className="text-xs text-purple-100 mt-0.5">Complete logic — assignment, goods returns, cancellation &amp; advances</p>
            </div>
            <button onClick={() => setShowHelpModal(false)} className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-all">
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 text-sm">
            {[
              {
                icon: '🎯', title: '1. Commission (TT) Assignment',
                points: [
                  'TT users assign commission per item: percentage or fixed ₹, calculated on item value EXCLUDING GST, after discount. It can never exceed the ex-GST value.',
                  '<b>Example:</b> Tile ₹6,530 × 11 boxes = ₹71,830 · 18% GST → ex-GST ₹60,872.88 · at 10% → <b>commission ₹6,087.29</b>.',
                  'Commission is assigned on NET quantity — returned boxes never earn commission.',
                  'Orders created by TT users are system-managed; others are assignable by TT users.',
                ],
              },
              {
                icon: '↩️', title: '2. Goods Returns',
                points: [
                  '<b>Returned + commission UNPAID</b> → the payable drops, net of the return. Nothing to collect.',
                  '<b>Example:</b> 1 of 11 boxes returned (₹553.39 worth) → payable becomes ₹6,087.29 − ₹553.39 = <b>₹5,533.90</b>. Nothing to collect.',
                  '<b>Returned + commission ALREADY PAID</b> → that portion becomes a Receivable owed by the reference.',
                  '<b>Example:</b> reference was paid ₹2,000 for goods later fully returned and advance did not cover it → <b>₹2,000 Receivable</b>.',
                ],
              },
              {
                icon: '🚫', title: '3. Cancelled Delivery Orders',
                points: [
                  '<b>Cancelled + UNPAID</b> → commission becomes ₹0 everywhere. No trace in payouts.',
                  '<b>Cancelled + PAID ₹10,000</b> → a ₹10,000 Receivable entry is created for that reference.',
                  'Cancellation is a bookkeeping change — commission is never deducted at assignment, so nothing needs unwinding.',
                ],
              },
              {
                icon: '🧾', title: '4. Receivable — One Unified Ledger',
                points: [
                  'Every rupee owed back lives here, from two sources: Cancelled DO overpayments & Goods-Return recovery.',
                  'It reduces what you pay the reference next, or is collected as cash via "Record Recovery".',
                  'Every create / deduct / restore / cash event is audit-logged on the entry.',
                ],
              },
              {
                icon: '✂️', title: '5. Claiming the Receivable at Payment',
                points: [
                  'Commission is stored GROSS. The reference’s outstanding Receivable is claimed when a commission payment is recorded — never deducted at assignment.',
                  'In "Record Payment" the "CLAIM FROM RECEIVABLE" option is always on: the payable settles the receivable first, any remainder is paid as cash.',
                  '<b>Example (receivable ≥ payment):</b> receivable ₹500, recording ₹300 → claim ₹300, cash ₹0, receivable left ₹200.',
                  '<b>Example (receivable &lt; payment):</b> receivable ₹500, recording ₹2,000 → claim ₹500, cash ₹1,500, receivable ₹0.',
                  'When the receivable is smaller than the payment you choose: pay the remainder now, or leave it partial (pay in another memo).',
                ],
              },
              {
                icon: '💵', title: '6. Payments & Cash Recovery',
                points: [
                  '"Record Payment" records the pending commission — the outstanding Receivable is claimed first, the rest is paid as cash.',
                  '<b>Example:</b> pending ₹1,500, receivable ₹500 → claim ₹500, reference receives ₹1,000 cash.',
                  '"Record Recovery" collects Receivable amounts directly by cash, per entry, any time.',
                  'Deleting a payment record rolls back its linked ledger effects automatically (including the claim).',
                ],
              },
              {
                icon: '🔄', title: '7. Status Changes — Full Lifecycle Examples',
                points: [
                  '<b>Pending → Completed:</b> commission stays gross; nothing is deducted at completion (claiming happens at payout).',
                  '<b>Pending → Cancelled, unpaid:</b> commission vanishes from all reports.',
                  '<b>Pending → Cancelled, paid ₹3,000:</b> ₹3,000 added to Receivable for that reference.',
                  '<b>Reset by company head:</b> assigned commission zeroed; if it was paid, that amount moves to Receivable.',
                  '<b>Completed again after reset:</b> fresh commission is assigned; the Receivable is claimed when commission is paid.',
                ],
              },
              {
                icon: '🏷️', title: '8. Filters & Columns',
                points: [
                  'No Commission / To Be Paid / Paid / Receivable — see filter counts on the status bar.',
                  'Columns: Commission (net, what reference earned) · Paid (handed over) · Receivable (owed back).',
                  'Header bar totals show everything at a glance; data auto-refreshes every 60 seconds.',
                ],
              },
              {
                icon: '🔄', title: '9. Reference Transfer',
                points: [
                  '<b>Transfer Reference</b> moves a DO from one reference to another with full financial rebalancing.',
                  '<b>Example:</b> DO with ₹6,087.29 commission assigned to Reference A transfers to B — the commission moves gross, and B\u2019s outstanding is claimed when B\u2019s commission is paid.',
                  '<b>Paid commission:</b> if ₹3,000 was already paid to A, a ₹3,000 receivable is created for A on transfer (he owes it back). B gets the DO fresh.',
                  '<b>GR debts move too:</b> any goods-return recovery entries transfer to the new reference — benefits follow the DO.',
                  'Cancelled DOs cannot be transferred. The Transfer button appears on every DO in the reference details modal.',
                ],
              },
            ].map(section => (
              <div key={section.title} className={`p-4 rounded-xl border ${
                isDarkMode ? 'bg-gray-750 border-gray-600/60' : 'bg-gray-50 border-gray-200'
              }`}>
                <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                  {section.icon} {section.title}
                </h3>
                <ul className={`space-y-1.5 list-disc list-inside ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {section.points.map((p2, i) => <li key={i} dangerouslySetInnerHTML={{ __html: p2 }} />)}
                </ul>
              </div>
            ))}
            <p className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Note: data on this page refreshes automatically every 60 seconds. Changes made elsewhere (order cancellation, reset) appear here within 2 minutes.
            </p>
          </div>

          {/* Footer */}
          <div className={`px-6 py-3 border-t flex justify-end ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setShowHelpModal(false)}
              className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
};

export default ReferenceCommission;